<template>
  <div>
    <navbar-dashboard name="Nuevo envío"></navbar-dashboard>
    <div v-if="isLoadingPage" class="text-center d-flex justify-content-center ew-loading-indicator">
      <div class="align-self-center inner-container">
        <img src="@/assets/images/sack/loading.gif" alt="Cargando...">
        <div class="mt-5 h3 font-weight-bold">Procesando tu solicitud, esto tomará solo un momento...</div>
      </div>
    </div>
    <main class="flex-grow py-4 ew-shipment-main" v-if="!isLoadingPage">
      <div class="ew-stepper-wrap">
        <ol class="ew-stepper list-unstyled">
          <li v-for="s in wizardSteps" :key="s.id" class="ew-stepper__item"
            :class="{ 'is-done': s.id < step, 'is-active': s.id === step }">
            <button type="button" class="ew-stepper__node" @click="goToStep(s.id)"
              v-b-tooltip.hover.top="s.id < step ? ('Volver al paso ' + s.id) : s.label">
              <span class="ew-stepper__dot">
                <i v-if="s.id < step" class="bi bi-check-lg"></i>
                <span v-else>{{ s.id }}</span>
              </span>
              <span class="ew-stepper__label">{{ s.label }}</span>
            </button>
          </li>
        </ol>
      </div>

      <aside class="ew-summary-aside">
        <div class="card shadow-sm border-0">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-baseline mb-3">
              <h6 class="mb-0 font-weight-bold">Resumen del envío</h6>
              <small class="text-muted">Paso {{ step }} de 4</small>
            </div>

            <div class="ew-summary-route" v-if="origin_direction.city || destination_direction.city">
              <div class="ew-rp">
                <strong>{{ origin_direction.city || 'Origen' }}</strong>
                <small>{{ origin_direction.country_code || 'MX' }}<span v-if="origin_direction.postal_code"> · {{ origin_direction.postal_code }}</span></small>
              </div>
              <i class="bi bi-arrow-right text-muted"></i>
              <div class="ew-rp">
                <strong>{{ destination_direction.city || 'Destino' }}
                  <span v-if="isInternational" class="badge badge-info ml-1">{{ destination_direction.country_code }}</span>
                </strong>
                <small v-if="destination_direction.postal_code">{{ destination_direction.postal_code }}</small>
              </div>
            </div>
            <p v-else class="text-muted small mb-3">Completa origen y destino para ver el resumen aquí.</p>

            <div class="ew-summary-meta" v-if="parcels.length">
              <div class="ew-mc">
                <strong>{{ totalWeightKg }} kg</strong>
                <small>Peso</small>
              </div>
              <div class="ew-mc">
                <strong>{{ tipoEnvioLabel }}</strong>
                <small>Tipo</small>
              </div>
              <div class="ew-mc">
                <strong>{{ cantidadGuias }}</strong>
                <small>{{ Number(cantidadGuias) == 1 ? 'Paquete' : 'Paquetes' }}</small>
              </div>
            </div>

            <div class="ew-summary-price" v-if="!hasRole(['user'])">
              <template v-if="step == 1">
                <div class="ew-pb-label">Cotización</div>
                <div class="ew-pb-value ew-pb-value--muted">Aún sin cotizar</div>
              </template>
              <template v-else-if="step == 2">
                <div class="ew-pb-label">Tarifas disponibles</div>
                <div class="ew-pb-value">{{ coveredOptionsCount }}</div>
                <div class="ew-pb-sub" v-if="cheapestOptionPrice !== null">Desde ${{ cheapestOptionPrice }} MXN</div>
              </template>
              <template v-else>
                <div class="ew-pb-label">{{ step == 4 ? 'Total a pagar' : 'Servicio seleccionado' }}</div>
                <div class="ew-pb-value">${{ selectedService.total_amount || '0.00' }} {{ selectedService.currency || 'MXN' }}</div>
                <div class="ew-pb-sub">{{ selectedService.carrier }} · {{ selectedService.carrier_service_name }}</div>
              </template>
            </div>
          </div>
        </div>
      </aside>

      <!-- Paso 1 -->
      <div class="container ew-step-col" v-if="step == 1">
        <!-- Header -->
        <div
          class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4">
          <div>
            <h1 class="ew-page-title">Cotizador de envíos</h1>
            <p class="text-muted">Compara precios y tiempos de entrega de múltiples paqueterías</p>
          </div>
        </div>

        <!-- Modal para crear perfil -->
        <b-modal ref="addProfile" hide-footer title="Crear nuevo perfil de paquete">
          <form @submit.prevent="saveProfile">
            <div class="form-group mb-3">
              <label for="">Nombre del perfil *</label>
              <input type="text" class="form-control" v-model="newProfile.name" required placeholder="Ej: Caja chica"
                v-b-tooltip.hover.top="'Asigna un nombre para identificar este paquete fácilmente'" />
            </div>

            <div class="form-group mb-3">
              <label for="">Tipo de envío *</label>
              <select class="form-control" v-model="newProfile.type" required
                v-b-tooltip.hover.top="'Selecciona el formato de tu mercancía'">
                <option disabled value="">-- Selecciona un tipo --</option>
                <option value="package">Paquete</option>
                <option value="document">Documento</option>
                <option value="tarima">Tarima</option>
              </select>
            </div>

            <div class="row" v-if="newProfile.type == 'package'">
              <div class="col-md-3 mb-3">
                <label for="pesoPackage">Peso (kg) *</label>
                <input id="pesoPackage" name="pesoPackage" type="number" step="1" min="1" max="60" class="form-control"
                  v-model.number="newProfile.weight" required @change="newProfile.weight = Math.ceil(newProfile.weight)"
                  v-b-tooltip.hover.top="'Peso en kg (Redondeado hacia arriba, máximo 60 kg)'" />
                <small class="form-text text-muted">Máximo 60 kg (Sin decimales)</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="largoPackageForm">Largo (cm) *</label>
                <input id="largoPackageForm" name="largoPackageForm" type="number" min="1" max="150"
                  class="form-control" v-model="newProfile.dimensions.length" required
                  v-b-tooltip.hover.top="'Largo en cm (Máximo 150 cm)'" />
                <small class="form-text text-muted">Máximo 150 cm</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="anchoPackageForm">Ancho (cm) *</label>
                <input id="anchoPackageForm" name="anchoPackageForm" type="number" min="1" max="100"
                  class="form-control" v-model="newProfile.dimensions.width" required
                  v-b-tooltip.hover.top="'Ancho en cm (Máximo 100 cm)'" />
                <small class="form-text text-muted">Máximo 100 cm</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="altoPackageForm">Alto (cm) *</label>
                <input id="altoPackageForm" name="altoPackageForm" type="number" min="1" max="100" class="form-control"
                  required v-model="newProfile.dimensions.height"
                  v-b-tooltip.hover.top="'Alto en cm (Máximo 100 cm)'" />
                <small class="form-text text-muted">Máximo 100 cm</small>
              </div>
            </div>

            <div class="row" v-if="newProfile.type == 'document'">
              <div class="col-md-3 mb-3">
                <label for="pesoDocumentForm">Peso (kg) *</label>
                <input id="pesoDocumentForm" name="pesoDocumentForm" type="number" step="0.1" min="0.1" max="0.5"
                  class="form-control" v-model="newProfile.weight" required
                  v-b-tooltip.hover.top="'Peso en kg (Máximo 0.5 kg)'" />
                <small class="form-text text-muted">Máximo 0.5 kg</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="largoDocumentForm">Largo (cm) *</label>
                <input id="largoDocumentForm" name="largoDocumentForm" type="number" min="1" max="35"
                  class="form-control" v-model="newProfile.dimensions.length" required
                  v-b-tooltip.hover.top="'Largo en cm (Máximo 35 cm)'" />
                <small class="form-text text-muted">Máximo 35 cm</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="anchoDocumentForm">Ancho (cm) *</label>
                <input id="anchoDocumentForm" name="anchoDocumentForm" type="number" min="1" max="24"
                  class="form-control" v-model="newProfile.dimensions.width" required
                  v-b-tooltip.hover.top="'Ancho en cm (Máximo 24 cm)'" />
                <small class="form-text text-muted">Máximo 24 cm</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="altoDocumentForm">Alto (cm) *</label>
                <input id="altoDocumentForm" name="altoDocumentForm" type="number" step="0.1" min="0.1" max="1.5"
                  class="form-control" :class="{ 'is-invalid': envelopeTooThick(newProfile.dimensions.height) }"
                  v-model="newProfile.dimensions.height" required />
                <div v-if="envelopeTooThick(newProfile.dimensions.height)" class="invalid-feedback">
                  Un sobre no puede medir más de 1.5 cm de alto.
                </div>
                <small v-else class="form-text text-muted">Máximo 1.5 cm</small>
              </div>
            </div>

            <div class="row" v-if="newProfile.type == 'tarima'">
              <div class="col-md-3 mb-3">
                <label for="pesoTarimaForm">Peso (kg) *</label>
                <input id="pesoTarimaForm" name="pesoTarimaForm" type="number" step="0.1" min="1" max="2000"
                  class="form-control" v-model="newProfile.weight" required
                  v-b-tooltip.hover.top="'Peso en kg (Máximo 2000 kg)'" />
                <small class="form-text text-muted">Máximo 2000 kg</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="largoTarimaForm">Largo (cm) *</label>
                <input id="largoTarimaForm" name="largoTarimaForm" type="number" min="1" max="300" class="form-control"
                  required v-model="newProfile.dimensions.length"
                  v-b-tooltip.hover.top="'Largo en cm (Máximo 300 cm)'" />
                <small class="form-text text-muted">Máximo 300 cm</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="anchoTarimaForm">Ancho (cm) *</label>
                <input id="anchoTarimaForm" name="anchoTarimaForm" type="number" min="1" max="140" class="form-control"
                  required v-model="newProfile.dimensions.width"
                  v-b-tooltip.hover.top="'Ancho en cm (Máximo 140 cm)'" />
                <small class="form-text text-muted">Máximo 140 cm</small>
              </div>
              <div class="col-md-3 mb-3">
                <label for="altoTarimaForm">Alto (cm) *</label>
                <input id="altoTarimaForm" name="altoTarimaForm" type="number" min="1" max="190" class="form-control"
                  required v-model="newProfile.dimensions.height"
                  v-b-tooltip.hover.top="'Alto en cm (Máximo 190 cm)'" />
                <small class="form-text text-muted">Máximo 190 cm</small>
              </div>
            </div>

            <div class="form-group mb-3">
              <label for="">Descripción</label>
              <input type="text" class="form-control" v-model="newProfile.description"
                placeholder="Ej: Caja ligera para ropa"
                v-b-tooltip.hover.top="'Agrega una breve descripción del contenido'" />
            </div>

            <div class="text-right">
              <button type="button" class="btn btn-outline-secondary" @click="hideAddProfileModal"
                v-b-tooltip.hover.top="'Cancelar la creación del perfil'">Cancelar</button>
              <button class="btn btn-primary" v-b-tooltip.hover.top="'Guardar el perfil creado'"
                type="submit">Guardar</button>
            </div>


          </form>
        </b-modal>
        <!-- Modal para crear direccion -->
        <b-modal ref="addAddress" size="lg" hide-footer title="Crear nueva dirección de envío">
          <form @submit.prevent="saveAddress">
            <div class="p-3 form-scroll">

              <div class="form-group">
                <label for="">Compañia <sup>*</sup></label>
                <input type="text" class="form-control" v-model="newAddress.company" required maxlength="100"
                  v-b-tooltip.hover.top="'Nombre de la empresa'">
              </div>

              <div class="form-row">
                <div class="form-group col-md-6">
                  <label for="">Nombre y apellido <sup>*</sup></label>
                  <input type="text" class="form-control" v-model="newAddress.name" required minlength="3"
                    maxlength="100" v-b-tooltip.hover.top="'Nombre completo de quien recibe o envía'">
                </div>
                <div class="form-group col-md-6">
                  <label for="">Teléfono <sup>*</sup></label>
                  <input type="text" class="form-control" v-model="newAddress.phone" required minlength="7"
                    maxlength="15" v-b-tooltip.hover.top="'Teléfono de contacto a 10 dígitos'">
                </div>
              </div>

              <div class="form-group">
                <label for="">Correo <sup>*</sup></label>
                <input type="email" class="form-control" v-model="newAddress.email" required maxlength="100"
                  v-b-tooltip.hover.top="'Correo electrónico para notificaciones'">
              </div>

              <div class="form-group">
                <label for="">Calle <sup>*</sup></label>
                <input type="text" class="form-control" v-model="newAddress.address" required minlength="3"
                  maxlength="150" v-b-tooltip.hover.top="'Nombre de la calle'">
              </div>

              <div class="form-row">
                <div class="form-group col-md-6">
                  <label for="">No. Ext <sup>*</sup></label>
                  <input type="text" class="form-control" v-model="newAddress.number" required maxlength="10"
                    v-b-tooltip.hover.top="'Número exterior'">
                </div>
                <div class="form-group col-md-6">
                  <label for="">No. Int</label>
                  <input type="text" class="form-control" v-model="newAddress.intNumber" maxlength="10"
                    v-b-tooltip.hover.top="'Número interior o departamento'">
                </div>
              </div>

              <div class="form-group">
                <label for="">Referencia <sup>*</sup></label>
                <input type="text" class="form-control" v-model="newAddress.reference_of_origin" required
                  maxlength="150" v-b-tooltip.hover.top="'Alguna referencia para localizar el domicilio'">
              </div>

              <country-select label="País" v-model="newAddress.country_code" :options="countryOptions"
                tooltip="País de esta dirección" />

              <div class="form-group position-relative" v-if="newAddress.country_code === 'MX'">
                <label for="">Código Postal <sup>*</sup></label>
                <input type="text" autocomplete="off" class="form-control" placeholder="C.P."
                  v-model="originAddress.input" @keyup="checkAddress" required minlength="4" maxlength="10"
                  v-b-tooltip.hover.top="'Ingresa el CP para autocompletar colonia y ciudad'">
                <div v-if="isLoadingAddress && isOpenAddress" class="autocomplete-results shadow rounded">
                  <ul class="list-group list-group-flush">
                    <li v-for="(result, i) in results" :key="i" @click="setResultAddress(result)"
                      class="list-group-item list-group-item-action" :class="{ active: i === arrowCounter }">
                      {{ result.postal_code }} - {{ result.place_name }} - {{ result.city }}
                    </li>
                  </ul>
                </div>
              </div>

              <div class="form-row" v-if="newAddress.country_code === 'MX'">
                <div class="form-group col-md-6">
                  <label for="">Colonia <sup>*</sup></label>
                  <input type="text" class="form-control" disabled v-model="newAddress.colony" required maxlength="100"
                    v-b-tooltip.hover.top="'Colonia seleccionada automáticamente'">
                </div>
                <div class="form-group col-md-6">
                  <label for="">Ciudad <sup>*</sup></label>
                  <input type="text" class="form-control" disabled v-model="newAddress.city" required maxlength="100"
                    v-b-tooltip.hover.top="'Ciudad seleccionada automáticamente'">
                </div>
              </div>

              <div class="form-row" v-else>
                <div class="form-group col-md-4">
                  <label for="">Código Postal <sup>*</sup></label>
                  <input type="text" class="form-control" v-model="newAddress.postal_code" required maxlength="10"
                    v-b-tooltip.hover.top="'Código postal de la dirección'">
                </div>
                <div class="form-group col-md-4">
                  <label for="">Colonia</label>
                  <input type="text" class="form-control" v-model="newAddress.colony" maxlength="100"
                    v-b-tooltip.hover.top="'Colonia, distrito o barrio (opcional según el país)'">
                </div>
                <div class="form-group col-md-4">
                  <label for="">Ciudad <sup>*</sup></label>
                  <input type="text" class="form-control" v-model="newAddress.city" required maxlength="100"
                    v-b-tooltip.hover.top="'Ciudad de la dirección'">
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end border-top pt-3 mt-3">
              <button type="button" class="btn btn-outline-secondary mr-2" @click="hideAddAddressModal"
                v-b-tooltip.hover.top="'Cancelar creación'">Cancelar</button>
              <button type="submit" class="btn btn-primary" v-b-tooltip.hover.top="'Guardar nueva dirección'">Guardar
                cambios</button>
            </div>
          </form>
        </b-modal>
        <!-- Modal de Catálogo de Ocurres -->
        <b-modal id="modal-ocurres" title="Buscar Sucursal Ocurre" size="lg" hide-footer>
          <div class="form-group">
            <label for="filtroCiudad">Buscar por Ciudad, Estado, Paquetería o CP:</label>
            <input type="text" id="filtroCiudad" v-model="filtroCiudad" @input="onSearchInput" class="form-control"
              placeholder="Escribe al menos 3 letras (ej. Acapulco, Fedex, 72000)..." autocomplete="off" />
          </div>

          <div class="table-responsive" style="max-height: 350px; overflow-y: auto;">
            <table class="table table-hover table-sm">
              <thead>
                <tr>
                  <th>Paquetería</th>
                  <th>Ciudad / Estado</th>
                  <th>Sucursal / Dirección</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ocurre, index) in ocurresCatalog" :key="index">
                  <td><span class="badge badge-secondary">{{ ocurre.companyName }}</span></td>
                  <td>{{ ocurre.city }}, {{ ocurre.state }}</td>
                  <td>
                    <strong>{{ ocurre.full_name }}</strong><br>
                    <small class="text-muted">{{ ocurre.address }}, C.P. {{ ocurre.postal_code }}</small>
                  </td>
                  <td>
                    <button class="btn btn-sm btn-primary" @click="selectOcurreBranch(ocurre)">
                      Seleccionar
                    </button>
                  </td>
                </tr>
                <tr v-if="ocurresCatalog.length === 0">
                  <td colspan="4" class="text-center text-muted py-3">
                    {{ filtroCiudad ? 'No se encontraron sucursales.' : 'Escribe algo para comenzar la búsqueda.' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-modal>
        <!-- Card -->
        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <h3 class="h5 mb-3 d-flex flex-column align-items-start">
              <div class="d-flex align-items-center mb-2">
                <i class="bi bi-geo-alt-fill text-primary mr-2"></i>
                Dirección de Origen y Destino
              </div>
              <button class="btn btn-outline-primary btn-sm d-flex align-items-center" @click="showAddAddressModal"
                v-b-tooltip.hover.top="'Agrega una nueva dirección de envío para usarla en tus cotizaciones'">
                <i class="bi bi-plus-circle mr-1"></i> Nueva dirección
              </button>
            </h3>
            <!-- Selector de Tipo de Entrega (Domicilio vs Ocurre) -->
            <div class="mb-3">
              <label class="font-weight-bold d-block">Tipo de Entrega en Destino</label>
              <switch-toggle :value="deliveryType" :options="deliveryTypeOptions" @input="setDeliveryType" />
            </div>
            <hr>
            <div class="row">
              <div class="col-md-6 mb-3">
                <h4 class="border-bottom pb-2 text-primary d-flex align-items-center">
                  <i class="bi bi-geo-alt-fill mr-2"></i> Origen
                </h4>

                <switch-toggle v-model="originAddressMode" :options="addressModeOptions" class="mb-3" />

                <div v-show="originAddressMode === 'saved'" class="form-group position-relative"
                  v-click-outside="closeDropdown">
                  <label for="">Seleccionar Dirección</label>
                  <input type="text" v-model="searchKeywordOrigin" @input="debounceSearch"
                    @focus="showDropdown = true; originAddressArrow = -1"
                    @keydown.down.prevent="moveOriginAddressArrow(1)" @keydown.up.prevent="moveOriginAddressArrow(-1)"
                    @keydown.enter.prevent="selectOriginAddressAtArrow" autocomplete="off"
                    placeholder="Escribe para buscar..." class="form-control"
                    v-b-tooltip.hover.top="'Busca y selecciona una dirección de origen guardada'" />

                  <ul v-show="showDropdown" class="list-group position-absolute w-100 mt-1 shadow-sm ew-address-list"
                    @scroll="onScroll">
                    <li v-for="(item, i) in optionsOrigen" :key="item.id" @mousedown.prevent="handleSelectOrigin(item)"
                      class="list-group-item list-group-item-action" :class="{ active: i === originAddressArrow }">
                      {{ item.text }}
                    </li>
                    <li v-if="optionsOrigen.length === 0 && !isLoading" class="list-group-item text-muted">
                      No se encontraron resultados</li>
                    <li v-if="isLoading" class="list-group-item text-center bg-light"><small>Cargando más...</small></li>
                  </ul>
                </div>

                <div v-if="originAddressMode === 'saved' && originAddressSelected" class="ew-selected-address mt-2">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <strong class="text-primary small">
                      <i class="bi bi-check-circle-fill mr-1"></i> Dirección seleccionada
                    </strong>
                    <button type="button" class="btn btn-sm btn-link text-muted p-0"
                      @click="clearOriginSelectedAddress" v-b-tooltip.hover.top="'Cambiar de dirección'">
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                  <p class="mb-1 small" v-if="origin_direction.companyName">
                    <i class="bi bi-building mr-2 text-muted"></i>{{ origin_direction.companyName }}
                  </p>
                  <p class="mb-1 small"><i class="bi bi-person-fill mr-2 text-muted"></i>{{ origin_direction.full_name }}
                  </p>
                  <p class="mb-1 small"><i class="bi bi-telephone-fill mr-2 text-muted"></i>{{ origin_direction.phone }}
                  </p>
                  <p class="mb-1 small" v-if="origin_direction.email">
                    <i class="bi bi-envelope-fill mr-2 text-muted"></i>{{ origin_direction.email }}
                  </p>
                  <p class="mb-1 small"><i class="bi bi-geo-alt-fill mr-2 text-muted"></i>{{ originStreetLine }}</p>
                  <p class="mb-0 small"><i class="bi bi-pin-map-fill mr-2 text-muted"></i>{{ originLocationLine }}</p>
                </div>

                <div v-show="originAddressMode === 'new'">
                  <country-select id="origenCountry" label="País de origen" v-model="origin_direction.country_code"
                    :options="countryOptions" tooltip="País de la dirección de origen" />
                  <div class="form-group mt-2 position-relative" v-if="origin_direction.country_code === 'MX'">
                    <label for="origenPostal">Ingresar Código Postal</label>
                    <input type="text" v-model="selectedOrigen" id="origenPostal" class="form-control"
                      placeholder="C.P. Origen" @keyup="checkAddressOrigin" @keyup.down="onArrowDown"
                      @keyup.up="onArrowUp" @keyup.enter="onEnter" autocomplete="off"
                      v-b-tooltip.hover.top="'Ingresa el código postal de origen para validar la zona'" />

                    <div v-if="isLoading" class="spinner-border spinner-border-sm text-primary position-absolute"
                      style="top: 40px; right: 15px;" role="status">
                      <span class="sr-only">Cargando...</span>
                    </div>

                    <ul v-if="results.length && isOpen" class="list-group position-absolute w-100 mt-1 shadow-sm"
                      style="z-index: 1000; max-height: 200px; overflow-y: auto;">
                      <li v-for="(result, i) in results" :key="i" @click="setResult(result)"
                        class="list-group-item list-group-item-action" :class="{ active: i === arrowCounter }">
                        {{ result.postal_code }} - {{ result.place_name }} - {{ result.city }}
                      </li>
                    </ul>
                  </div>
                  <div class="mt-2" v-else>
                    <div class="form-group">
                      <label for="origenPostalManual">Código Postal</label>
                      <input type="text" id="origenPostalManual" class="form-control"
                        v-model="origin_direction.postal_code" placeholder="Código postal"
                        v-b-tooltip.hover.top="'Código postal de origen (entrada manual, sin catálogo para este país)'" />
                    </div>
                    <div class="form-group">
                      <label for="origenCiudadManual">Ciudad</label>
                      <input type="text" id="origenCiudadManual" class="form-control" v-model="origin_direction.city"
                        placeholder="Ciudad" v-b-tooltip.hover.top="'Ciudad de origen'" />
                    </div>
                    <div class="form-group">
                      <label for="origenEstadoManual">Estado / Provincia</label>
                      <country-select v-if="originRegions.length" id="origenEstadoManual"
                        :value="origin_direction.state_short" :options="originRegions"
                        placeholder="Selecciona un estado" @input="onRegionSelect('origin_direction', originRegions, $event)"
                        tooltip="Estado de origen (requerido por algunos carriers para generar la guía)" />
                      <input v-else type="text" id="origenEstadoManualTexto" class="form-control"
                        v-model="origin_direction.state" placeholder="Estado o provincia"
                        v-b-tooltip.hover.top="'Estado o provincia de origen'" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <h4 class="border-bottom pb-2 text-primary d-flex align-items-center">
                  <i class="bi bi-geo-alt-fill mr-2"></i> Destino
                </h4>

                <!-- INDICADOR DE OCURRE (SE MUESTRA SOLO SI ES OCURRE) -->
                <div v-if="deliveryType === 'ocurre'" class="alert alert-info">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h5 class="alert-heading mb-0">
                      <i class="bi bi-shop mr-1"></i> Envío a Ocurre (Sucursal)
                    </h5>
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="openOcurreModal">
                      <i class="bi bi-arrow-repeat"></i> Cambiar Sucursal
                    </button>
                  </div>

                  <hr class="my-2">

                  <div class="small text-dark">
                    <p class="mb-1">
                      <strong>Paquetería:</strong> <span class="badge badge-primary">{{
                        destination_direction.companyName }}</span>
                    </p>
                    <p class="mb-1">
                      <strong>Sucursal:</strong> <span>{{ destination_direction.full_name }}</span>
                    </p>
                    <p class="mb-1">
                      <strong>Dirección:</strong> <span>{{ destination_direction.address }}, Col. {{
                        destination_direction.colony }}, C.P. {{ destination_direction.postal_code }}</span>
                    </p>
                    <p class="mb-0">
                      <strong>Ciudad / Estado:</strong> <span>{{ destination_direction.city }}, {{
                        destination_direction.state }} ({{ destination_direction.state_short }})</span>
                    </p>
                  </div>
                </div>
                <!-- FLUJO NORMAL (SE OCULTA SI ES OCURRE) -->
                <div v-else>
                  <switch-toggle v-model="destinationAddressMode" :options="addressModeOptions" class="mb-3" />

                  <div v-show="destinationAddressMode === 'saved'" class="form-group position-relative"
                    v-click-outside="closeDropdownDestination">
                    <label for="">Seleccionar Dirección</label>
                    <input type="text" v-model="searchKeywordDestination" @input="debounceSearchDestination"
                      @focus="showDropdownDestination = true; destinationAddressArrow = -1"
                      @keydown.down.prevent="moveDestinationAddressArrow(1)"
                      @keydown.up.prevent="moveDestinationAddressArrow(-1)"
                      @keydown.enter.prevent="selectDestinationAddressAtArrow" autocomplete="off"
                      placeholder="Escribe para buscar..." class="form-control"
                      v-b-tooltip.hover.top="'Busca y selecciona una dirección de destino guardada'" />

                    <ul v-show="showDropdownDestination"
                      class="list-group position-absolute w-100 mt-1 shadow-sm ew-address-list" @scroll="onScroll">
                      <li v-for="(item, i) in optionsDestination" :key="item.id"
                        @mousedown.prevent="handleSelectDestination(item)" class="list-group-item list-group-item-action"
                        :class="{ active: i === destinationAddressArrow }">
                        {{ item.text }}
                      </li>
                      <li v-if="optionsDestination.length === 0 && !isLoading" class="list-group-item text-muted">
                        No se encontraron resultados</li>
                    </ul>
                  </div>

                  <div v-if="destinationAddressMode === 'saved' && destinationAddressSelected"
                    class="ew-selected-address mt-2">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <strong class="text-primary small">
                        <i class="bi bi-check-circle-fill mr-1"></i> Dirección seleccionada
                      </strong>
                      <button type="button" class="btn btn-sm btn-link text-muted p-0"
                        @click="clearDestinationSelectedAddress" v-b-tooltip.hover.top="'Cambiar de dirección'">
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </div>
                    <p class="mb-1 small" v-if="destination_direction.companyName">
                      <i class="bi bi-building mr-2 text-muted"></i>{{ destination_direction.companyName }}
                    </p>
                    <p class="mb-1 small">
                      <i class="bi bi-person-fill mr-2 text-muted"></i>{{ destination_direction.full_name }}
                    </p>
                    <p class="mb-1 small">
                      <i class="bi bi-telephone-fill mr-2 text-muted"></i>{{ destination_direction.phone }}
                    </p>
                    <p class="mb-1 small" v-if="destination_direction.email">
                      <i class="bi bi-envelope-fill mr-2 text-muted"></i>{{ destination_direction.email }}
                    </p>
                    <p class="mb-1 small"><i class="bi bi-geo-alt-fill mr-2 text-muted"></i>{{ destinationStreetLine }}
                    </p>
                    <p class="mb-0 small"><i class="bi bi-pin-map-fill mr-2 text-muted"></i>{{ destinationLocationLine }}
                    </p>
                  </div>

                  <div v-show="destinationAddressMode === 'new'">
                    <country-select id="destinoCountry" label="País de destino"
                      v-model="destination_direction.country_code" :options="countryOptions"
                      tooltip="País de la dirección de destino" />
                    <div class="form-group mt-2 position-relative" v-if="destination_direction.country_code === 'MX'">
                      <label for="destinoPostal">Ingresar Código Postal</label>
                      <input type="text" v-model="selectedDestino" id="destinoPostal" class="form-control"
                        placeholder="C.P. Destino" @keyup="checkAddressDestination" @keyup.down="onArrowDownDes"
                        @keyup.up="onArrowUpDes" @keyup.enter="onEnterDes" autocomplete="off"
                        v-b-tooltip.hover.top="'Ingresa el código postal de destino'" />

                      <div v-if="isLoadingDes" class="spinner-border spinner-border-sm text-primary position-absolute"
                        style="top: 40px; right: 15px;" role="status">
                        <span class="sr-only">Cargando...</span>
                      </div>

                      <ul v-if="resultsDes.length && isOpenDes" class="list-group position-absolute w-100 mt-1 shadow-sm"
                        style="z-index: 1000; max-height: 200px; overflow-y: auto;">
                        <li v-for="(result, i) in resultsDes" :key="i" @click="setResultDes(result)"
                          class="list-group-item list-group-item-action" :class="{ active: i === arrowCounterDes }">
                          {{ result.postal_code }} - {{ result.place_name }} - {{ result.city }}
                        </li>
                      </ul>
                    </div>
                    <div class="mt-2" v-else>
                      <div class="form-group">
                        <label for="destinoPostalManual">Código Postal</label>
                        <input type="text" id="destinoPostalManual" class="form-control"
                          v-model="destination_direction.postal_code" placeholder="Código postal"
                          v-b-tooltip.hover.top="'Código postal de destino (entrada manual, sin catálogo para este país)'" />
                      </div>
                      <div class="form-group">
                        <label for="destinoCiudadManual">Ciudad</label>
                        <input type="text" id="destinoCiudadManual" class="form-control"
                          v-model="destination_direction.city" placeholder="Ciudad"
                          v-b-tooltip.hover.top="'Ciudad de destino'" />
                      </div>
                      <div class="form-group">
                        <label for="destinoEstadoManual">Estado / Provincia</label>
                        <country-select v-if="destinationRegions.length" id="destinoEstadoManual"
                          :value="destination_direction.state_short" :options="destinationRegions"
                          placeholder="Selecciona un estado"
                          @input="onRegionSelect('destination_direction', destinationRegions, $event)"
                          tooltip="Estado de destino (requerido por algunos carriers para generar la guía)" />
                        <input v-else type="text" id="destinoEstadoManualTexto" class="form-control"
                          v-model="destination_direction.state" placeholder="Estado o provincia"
                          v-b-tooltip.hover.top="'Estado o provincia de destino'" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h3 class="h5 mt-4 mb-3 d-flex align-items-center">
              <i class="bi bi-box-seam text-primary mr-2"></i> Tipo de Envío
            </h3>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="tipoEnvio" class="font-weight-bold">Selecciona el tipo de envío</label>
                <select id="tipoEnvio" v-model="tipoEnvio" class="form-control"
                  v-b-tooltip.hover.top="'Elige el formato de tu mercancía para ajustar las opciones de medida'">
                  <option disabled value="">-- Selecciona una opción --</option>
                  <option value="package">Paquete — Cajas, productos, artículos diversos</option>
                  <option value="document">Sobre — Documentos, cartas, papelería</option>
                  <option value="tarima">Tarima — Mercancía en pallets o tarimas, carga volumétrica</option>
                </select>
              </div>

              <!-- Selección de perfil de medidas -->
              <div class="col-md-6 mb-3">
                <label for="perfilPaquete" class="font-weight-bold d-flex align-items-center justify-content-between">
                  <span>Seleccionar perfil de paquete</span>
                  <button class="btn btn-outline-primary btn-sm d-flex align-items-center" @click="showAddProfileModal"
                    v-b-tooltip.hover.top="'Agrega un nuevo perfil de paquete para autocompletar las dimensiones en tus cotizaciones'">
                    <i class="bi bi-plus-circle mr-1"></i> Nuevo perfil
                  </button>
                </label>

                <div class="d-flex">
                  <select id="perfilPaquete" class="form-control" v-model="selectedProfile" @change="applyProfile"
                    v-b-tooltip.hover.top="'Selecciona un perfil guardado para cargar automáticamente peso y dimensiones'">
                    <option value="">-- Selecciona un perfil --</option>
                    <option v-for="profile in filteredProfiles" :key="profile._id" :value="profile._id">
                      {{ profile.name }} — {{ profile.dimensions.length }}x{{ profile.dimensions.width }}x{{
                        profile.dimensions.height }} cm — {{ profile.weight }} kg
                    </option>
                  </select>
                </div>

                <small class="form-text text-muted">
                  Selecciona uno de tus perfiles guardados para autocompletar las dimensiones.
                </small>
              </div>
            </div>

            <!-- Dimensiones -->
            <h3 class="h5 mt-4 mb-3 d-flex align-items-center">
              <i class="bi bi-calculator text-primary mr-2"
                v-b-tooltip.hover.top="'Ingresa las dimensiones y el peso exactos para obtener una cotización precisa'">
              </i>
              Dimensiones y Peso
            </h3>
            <div>
              <!-- Recorremos el array de paquetes -->
              <div v-for="(parcel, index) in parcels" :key="index" class="card p-3 mb-3 bg-light">
                <h4 class="h6 text-secondary mb-3">Paquete #{{ index + 1 }}</h4>

                <!-- Checkbox para copiar los datos del paquete original (solo a partir del segundo paquete) -->
                <div class="form-check mb-3" v-if="index > 0">
                  <input class="form-check-input" type="checkbox" :id="'copyOriginal-' + index"
                    v-model="parcel.copiarOriginal" @change="copiarDatosOriginales(index)" />
                  <label class="form-check-label" :for="'copyOriginal-' + index">
                    Copiar las mismas medidas y peso del Paquete #1
                  </label>
                </div>

                <div class="row" v-if="tipoEnvio == 'package'">
                  <div class="col-md-3 mb-3">
                    <label :for="'pesoPackage-' + index">Peso (kg) *</label>
                    <input :id="'pesoPackage-' + index" type="number" step="1" min="1" max="60" class="form-control"
                      v-model.number="parcel.peso" @change="parcel.peso = Math.ceil(parcel.peso)"
                      :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 60 kg (Sin decimales)</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'largoPackage-' + index">Largo (cm) *</label>
                    <input :id="'largoPackage-' + index" type="number" min="1" max="150" class="form-control"
                      v-model="parcel.largo" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 150 cm</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'anchoPackage-' + index">Ancho (cm) *</label>
                    <input :id="'anchoPackage-' + index" type="number" min="1" max="100" class="form-control"
                      v-model="parcel.ancho" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 100 cm</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'altoPackage-' + index">Alto (cm) *</label>
                    <input :id="'altoPackage-' + index" type="number" min="1" max="100" class="form-control"
                      v-model="parcel.alto" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 100 cm</small>
                  </div>
                </div>

                <div class="row" v-if="tipoEnvio == 'document'">
                  <div class="col-md-3 mb-3">
                    <label :for="'pesoDocument-' + index">Peso (kg) *</label>
                    <input :id="'pesoDocument-' + index" type="number" step="0.1" min="0.1" max="0.5"
                      class="form-control" v-model.number="parcel.peso" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 0.5 kg</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'largoDocument-' + index">Largo (cm) *</label>
                    <input :id="'largoDocument-' + index" type="number" min="1" max="35" class="form-control"
                      v-model="parcel.largo" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 35 cm</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'anchoDocument-' + index">Ancho (cm) *</label>
                    <input :id="'anchoDocument-' + index" type="number" min="1" max="24" class="form-control"
                      v-model="parcel.ancho" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 24 cm</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'altoDocument-' + index">Alto (cm) *</label>
                    <input :id="'altoDocument-' + index" type="number" step="0.1" min="0.1" max="1.5" class="form-control"
                      :class="{ 'is-invalid': envelopeTooThick(parcel.alto) }"
                      v-model="parcel.alto" :disabled="parcel.copiarOriginal" />
                    <div v-if="envelopeTooThick(parcel.alto)" class="invalid-feedback">
                      Un sobre no puede medir más de 1.5 cm de alto. Si es más grueso, envíalo como Paquete.
                    </div>
                    <small v-else class="form-text text-muted">Máximo 1.5 cm</small>
                  </div>
                </div>

                <div class="row" v-if="tipoEnvio == 'tarima'">
                  <div class="col-md-3 mb-3">
                    <label :for="'pesoTarima-' + index">Peso (kg) *</label>
                    <input :id="'pesoTarima-' + index" type="number" step="1" min="1" max="2000" class="form-control"
                      v-model.number="parcel.peso" @change="parcel.peso = Math.ceil(parcel.peso)"
                      :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 2000 kg (Sin decimales)</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'largoTarima-' + index">Largo (cm) *</label>
                    <input :id="'largoTarima-' + index" type="number" min="1" max="300" class="form-control"
                      v-model="parcel.largo" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 300 cm</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'anchoTarima-' + index">Ancho (cm) *</label>
                    <input :id="'anchoTarima-' + index" type="number" min="1" max="140" class="form-control"
                      v-model="parcel.ancho" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 140 cm</small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label :for="'altoTarima-' + index">Alto (cm) *</label>
                    <input :id="'altoTarima-' + index" type="number" min="1" max="190" class="form-control"
                      v-model="parcel.alto" :disabled="parcel.copiarOriginal" />
                    <small class="form-text text-muted">Máximo 190 cm</small>
                  </div>
                </div>

                <!-- Botón opcional para eliminar este paquete específico -->
                <button type="button" class="btn btn-sm btn-outline-danger" @click="removeParcel(index)"
                  v-if="parcels.length > 1">
                  Eliminar paquete
                </button>
              </div>

              <!-- Botón opcional para añadir más paquetes -->
              <button type="button" class="btn btn-primary mt-2" @click="addParcel" v-if="!isInternational">
                Agregar otro paquete
              </button>
              <small v-else class="form-text text-muted mt-2">
                Los envíos internacionales solo admiten 1 paquete por guía.
              </small>
              <div v-if="isInternational && totalWeightKg > 68" class="alert alert-warning mt-2 mb-0 py-2">
                <i class="bi bi-exclamation-triangle-fill mr-1"></i>
                El peso ({{ totalWeightKg }} kg) supera los 68 kg permitidos por FedEx International
                Priority/Economy — es posible que ese carrier no aparezca en la cotización u otro carrier
                sí lo acepte.
              </div>
            </div>

            <!-- Descripción -->
            <h3 class="h5 mt-4 mb-2 d-flex align-items-center">
              <i class="bi bi-file-text text-primary mr-2"
                v-b-tooltip.hover.top="'La descripción detallada ayuda a la paquetería a clasificar tu envío correctamente'">
              </i> Descripción del Contenido
            </h3>
            <div class="form-group mb-3">
              <label for="description_of_the_content">¿Qué estás enviando? *</label>
              <input id="description_of_the_content" name="description_of_the_content" type="text" class="form-control"
                placeholder="Ej: Documentos legales, contratos, facturas" v-model="description_of_the_content" required
                v-b-tooltip.hover.top="'Describe brevemente el contenido de tu paquete para fines de aduana y transporte'" />
              <small class="form-text text-muted">Esta información es requerida para el procesamiento del envío</small>
            </div>

            <!-- Valor declarado -->
            <div class="form-group mb-3">
              <label for="valor">Valor Declarado (MXN)</label>
              <input id="valor" name="valor" type="number" min="1" class="form-control" v-model="valorDeclarado"
                v-b-tooltip.hover.top="'Ingresa el valor comercial del paquete para determinar la cobertura del seguro'" />
              <small class="form-text text-muted">Valor comercial del contenido para efectos de seguro</small>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label class="d-block" for="bandera_condicion_pago">Condición de Pago (Solo requerido para
                    TRESGUERRAS)</label>
                  <div class="custom-control custom-checkbox mt-2">
                    <input type="checkbox" class="custom-control-input" id="bandera_condicion_pago"
                      v-model="bandera_condicion_pago" true-value="PC" false-value="CR">
                    <label class="custom-control-label" for="bandera_condicion_pago">
                      <strong>Flete Por Cobrar (PC)</strong>
                    </label>
                  </div>
                  <small class="form-text text-muted">Marca la casilla si aplica
                    como **Por Cobrar**.</small>
                </div>
              </div>
            </div>

            <!-- Botón -->
            <div class="text-center pt-3">
              <button class="btn btn-primary btn-lg w-50" style="border-radius: 0%;" @click="cotizar"
                v-b-tooltip.hover.top="'Compara precios y tiempos de entrega de múltiples paqueterías según las características de tu envío'">
                Cotizar Envío
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Paso 2 -->
      <div class="container ew-step-col" v-if="step == 2">
        <!-- Encabezado -->
        <div
          class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4">
          <div>
            <h1 class="ew-page-title">Cotizador de envíos</h1>
            <p class="text-muted mb-0">Compara precios y tiempos de entrega de múltiples paqueterías</p>
          </div>
          <button class="btn btn-outline-secondary d-flex align-items-center mt-3 mt-sm-0" @click="prevStep"
            v-b-tooltip.hover.top="'Reinicia el formulario para realizar una nueva cotización'">
            <i class="bi bi-plus mr-2"></i> Nueva Cotización
          </button>
        </div>
        <!-- Tabla de servicios -->
        <div class="card">
          <div class="card-header bg-white">
            <h5 class="mb-0">Comparación de Servicios</h5>
            <small class="text-muted">Selecciona el servicio que mejor se adapte a tus necesidades</small>
          </div>
          <div class="table-responsive">

            <table class="table table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th @click="changeSort('carrier')" style="cursor:pointer">
                    Paquetería <i :class="getSortIcon('carrier')"></i>
                  </th>
                  <th>Servicio</th>
                  <th @click="changeSort('time')" style="cursor:pointer">
                    Tiempo de Entrega <i :class="getSortIcon('time')"></i>
                  </th>
                  <th v-if="!hasRole(['user'])" @click="changeSort('price')" style="cursor:pointer">
                    Precio <i :class="getSortIcon('price')"></i>
                  </th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(so, i) in sortedShipmentOptions" :key="i">
                  <td>
                    <div class="d-flex align-items-center">
                      <img :src="so.carrier_logo_url_svg" alt="logo" class="img-fluid mr-3"
                        style="height:48px; width:48px; object-fit:contain;">
                      <div>
                        <div class="font-weight-bold">{{ so.carrier }}
                          <span v-if="so.rateCountry" class="badge badge-info ml-1">
                            <i class="bi bi-globe"></i> Internacional ({{ so.rateCountry }})
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="font-weight-bold">{{ so.carrier_service_name }}</div>
                  </td>
                  <td v-if="so.hasCoverage">
                    <i class="bi bi-clock mr-1"></i>
                    <p v-if="!so.is_remote">
                      <span v-if="so.total_transit_days">
                        {{ so.total_transit_days }} días hábiles de entrega
                      </span>
                      <span v-else>
                        {{ new Date(so.estimated_delivery).toLocaleDateString('es-MX', {
                          day: 'numeric', month: 'long',
                          year: 'numeric'
                        }) }}
                      </span>
                    </p>

                    <p v-if="so.is_remote">
                      Entrega en un área Remota
                    </p>
                  </td>
                  <td v-else>
                    <i class="bi bi-x-circle-fill text-danger mr-1"></i>
                    Sin cobertura para el destino
                  </td>
                  <td v-if="!hasRole(['user'])">
                    <div class="d-flex align-items-center">
                      <div class="font-weight-bold h5 mb-0">
                        <small>{{ so.currency }}</small>
                        {{ so.total_amount }}
                      </div>
                      <!-- Botón para ver desglose -->
                      <b-button variant="link" size="sm" class="p-0 ml-2" @click="abrirDesglose(so)"
                        v-b-tooltip.hover.top="'Ver desglose detallado de la tarifa'" v-if="so.hasCoverage">
                        <i class="bi bi-info-circle-fill"></i>
                      </b-button>
                    </div>
                    <small class="text-muted d-block" style="font-size: 0.7rem;">IVA incluido</small>
                  </td>

                  <td v-if="so.hasCoverage">
                    <div class="d-flex flex-column align-items-start gap-2">

                      <button class="btn btn-primary btn-sm d-flex align-items-center gap-1"
                        @click="setSelectedService(so)" title="Enviar"
                        v-b-tooltip.hover.top="'Seleccionar este servicio para enviar tu paquete'">
                        <i class="bi bi-send-fill"></i> Enviar
                      </button>

                      <template v-if="origin_direction.country_code === 'MX'">
                        <div class="form-check mb-2">
                          <input class="form-check-input" type="checkbox" v-model="so.requires_pickup"
                            :id="'pickup-' + i">
                          <label class="form-check-label small text-primary" :for="'pickup-' + i">
                            <i class="bi bi-truck"></i> Solicitar recolección
                          </label>
                        </div>

                        <div v-if="so.requires_pickup && canNotHasCustomPickup(so.carrier)"
                          class="alert alert-info p-1 mb-2" style="font-size: 0.75rem;">
                          <i class="bi bi-info-circle mr-1"></i>
                          Se usará la misma dirección de origen para la recolección.
                        </div>

                        <div v-if="so.requires_pickup && !canNotHasCustomPickup(so.carrier)"
                          class="alert alert-warning p-1 mb-2" style="font-size: 0.75rem;">
                          <i class="bi bi-geo-alt-fill mr-1"></i>
                          Podrás seleccionar una dirección de recolección distinta.
                        </div>
                      </template>
                      <small v-else class="text-muted d-block" style="font-size: 0.72rem; max-width: 180px;">
                        <i class="bi bi-info-circle mr-1"></i>
                        Recolección no disponible
                      </small>
                    </div>
                  </td>

                  <td v-else>
                    <button class="btn btn-secondary btn-sm" disabled title="No disponible"
                      v-b-tooltip.hover.top="'Este servicio no está disponible para el destino seleccionado'">
                      <i class="bi bi-x-circle-fill"></i> No disponible
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <b-modal ref="desgloseModal" title="Desglose de Tarifa" header-bg-variant="primary"
              header-text-variant="white" centered size="md" hide-footer>
              <div v-if="selectedRate" class="card shadow-sm">
                <div class="card-body">
                  <!-- Cabecera del Transportista -->
                  <div class="text-center mb-4">
                    <img :src="selectedRate.carrier_logo_url_svg" style="height: 45px; object-fit: contain;"
                      class="mb-2" alt="logo">
                    <h5 class="card-title mb-0">{{ selectedRate.carrier }}</h5>
                    <p class="text-muted small">{{ selectedRate.carrier_service_name }}</p>
                  </div>

                  <!-- Lista de Servicios -->
                  <ul class="list-group list-group-flush">
                    <li v-for="service in selectedRate.additionalServices" :key="service.id"
                      class="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span class="text-secondary">{{ service.service_name }}</span>
                      <span class="font-weight-bold">${{ service.price | formatPrice }}</span>
                    </li>

                    <!-- Total por guía -->
                    <li
                      class="list-group-item d-flex justify-content-between align-items-center px-0 border-top-0 pt-3">
                      <span class="h6 mb-0">Total por guía</span>
                      <span class="h5 text-primary mb-0 font-weight-bold">
                        {{ selectedRate.currency }} ${{ selectedRate.total_amount | formatPrice }}
                      </span>
                    </li>
                  </ul>

                  <!-- Alerta de Total Multiplicado -->
                  <div v-if="cantidadGuias > 1" class="alert alert-secondary mt-3 mb-0 border-0">
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Total por <span class="badge badge-dark">{{ cantidadGuias }}</span> guías:</span>
                      <h5 class="mb-0 font-weight-bold">
                        {{ selectedRate.currency }} ${{ (selectedRate.total_amount) | formatPrice }}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </b-modal>
            <b-modal ref="pickupModal" title="Configuración de Recolección" header-bg-variant="primary"
              header-text-variant="white" centered size="lg">
              <div class="p-2">
                <div class="d-flex align-items-center mb-4">
                  <div class="bg-light-primary p-3 rounded-circle mr-3" style="background-color: #e7f0ff;">
                    <i class="bi bi-truck text-primary h3 mb-0"></i>
                  </div>
                  <div>
                    <h6 class="font-weight-bold mb-1">Dirección de Recolección FedEx</h6>
                    <p class="text-muted small mb-0">Busca y selecciona el punto donde el mensajero debe recoger.</p>
                  </div>
                </div>

                <div class="form-group position-relative mb-4" v-if="hasCustomPickup">
                  <label for="vuscar direccion" class="font-weight-bold small text-uppercase text-muted">Buscar
                    Dirección</label>
                  <div class="input-group">
                    <div class="input-group-prepend">
                      <span class="input-group-text bg-white border-right-0">
                        <i class="bi bi-search text-muted"></i>
                      </span>
                    </div>
                    <input type="text" v-model="searchKeywordPickup" @input="debounceSearchPickup"
                      @focus="showDropdownPickup = true" placeholder="Escribe calle, alias o ciudad..."
                      class="form-control form-control-lg border-left-0" />
                  </div>

                  <div v-if="showDropdownPickup" class="list-dropdown-container shadow rounded border">
                    <div v-for="item in optionsPickup" :key="item.id" @click="handleSelectPickup(item)"
                      class="p-3 list-item-option border-bottom">
                      <div class="d-flex align-items-center">
                        <i class="bi bi-geo-alt mr-2 text-primary"></i>
                        <div>
                          <div class="font-weight-bold">{{ item.text }}</div>
                          <small class="text-muted">{{ item.subtext || 'Dirección de contacto' }}</small>
                        </div>
                      </div>
                    </div>
                    <div v-if="optionsPickup.length === 0 && !isLoading" class="p-4 text-center text-muted">
                      No se encontraron resultados
                    </div>
                    <div v-if="isLoading" class="text-center p-3 bg-light">
                      <small>Buscando...</small>
                    </div>
                  </div>
                </div>

                <div class="form-group mb-4">
                  <label for="fecha y rango" class="font-weight-bold small text-uppercase text-muted">Fecha y Rango
                    de Horario</label>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="input-group">
                        <div class="input-group-prepend">
                          <span class="input-group-text bg-white"><i
                              class="bi bi-calendar-event text-primary"></i></span>
                        </div>
                        <input type="date" v-model="pickupDate" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="input-group">
                        <div class="input-group-prepend">
                          <span class="input-group-text bg-white small">De</span>
                        </div>
                        <input type="time" v-model="readyByTime" class="form-control" />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="input-group">
                        <div class="input-group-prepend">
                          <span class="input-group-text bg-white small">A</span>
                        </div>
                        <input type="time" v-model="closeTime" class="form-control" />
                      </div>
                    </div>
                  </div>
                  <small class="text-muted mt-1 d-block">Asegúrate de dejar un rango mínimo de 2 a 4 horas según el
                    transportista.</small>
                </div>

                <div v-if="pickup_direction_id" class="card border-primary bg-light shadow-sm">
                  <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-start">
                      <span class="badge badge-primary mb-2">Punto de recolección</span>
                      <button type="button" class="btn btn-sm btn-outline-danger border-0"
                        @click="pickup_direction_id = null">
                        <i class="bi bi-x-circle"></i>
                      </button>
                    </div>
                    <div v-if="selectedPickupDetail">
                      <p class="mb-1 font-weight-bold text-primary">
                        <i class="bi bi-person-fill mr-1"></i> {{ selectedPickupDetail.name }}
                      </p>
                      <p class="mb-1 font-weight-bold">{{ selectedPickupDetail.address }}</p>
                      <p class="mb-0 small text-muted">
                        Col. {{ selectedPickupDetail.colony }}, {{ selectedPickupDetail.city }}, CP {{
                          selectedPickupDetail.postal_code }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <template #modal-footer="{ cancel }">
                <b-button variant="link" class="text-muted" @click="cancel()"
                  v-b-tooltip.hover.top="'Cancelar la selección de punto de recolección'">
                  Cancelar
                </b-button>
                <b-button variant="primary" class="px-4 shadow-sm" @click="confirmPickupAndContinue"
                  v-b-tooltip.hover.top="'Confirmar el punto de recolección seleccionado y continuar con el envío'">
                  Confirmar Recolección <i class="bi bi-arrow-right ml-1"></i>
                </b-button>
              </template>
            </b-modal>
          </div>
        </div>
      </div>
      <div class="container ew-step-col" v-if="step == 3">
        <form @submit.prevent="createShipment">
          <h3 class="mb-4 text-primary font-weight-bold">
            <i class="bi bi-geo-alt-fill"></i> Ingresar Direcciones
          </h3>

          <div class="row">
            <!-- ORIGEN -->
            <div class="col-md-6 mb-4">
              <div class="card shadow-sm border-0 rounded-lg">
                <div class="card-header bg-primary text-white">
                  <strong>Origen</strong>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label for="companyOrigen">Compañía</label>
                    <input type="text" id="companyOrigen" name="companyOrigen" class="form-control"
                      v-model="origin_direction.companyName" required
                      @input="origin_direction.companyName = limpiarCaracteresEspeciales(origin_direction.companyName)"
                      v-b-tooltip.hover.top="'Nombre de la empresa (si aplica, sin caracteres especiales)'">
                  </div>
                  <div class="form-group">
                    <label for="nombreOrigen">Nombre y Apellido</label>
                    <input type="text" id="nombreOrigen" name="nombreOrigen" class="form-control"
                      v-model="origin_direction.full_name" required
                      @input="origin_direction.full_name = limpiarCaracteresEspeciales(origin_direction.full_name)"
                      v-b-tooltip.hover.top="'Nombre completo (sin caracteres especiales)'">
                  </div>
                  <div class="form-group">
                    <label for="telefonoOrigen">Teléfono</label>
                    <input type="tel" id="telefonoOrigen" name="telefonoOrigen" class="form-control"
                      v-model="origin_direction.phone" required maxlength="10"
                      @input="origin_direction.phone = origin_direction.phone.replace(/\D/g, '').slice(0, 10)"
                      v-b-tooltip.hover.top="'Teléfono de contacto exacto a 10 dígitos'">
                    <small class="text-danger" v-if="origin_direction.phone && origin_direction.phone.length < 10">
                      Debe contener exactamente 10 dígitos.
                    </small>
                  </div>
                  <div class="form-group">
                    <label for="emailOrigen">Correo electrónico</label>
                    <input type="email" id="emailOrigen" name="emailOrigen" class="form-control"
                      v-model="origin_direction.email" required
                      v-b-tooltip.hover.top="'Correo para notificaciones de envío'">
                  </div>
                  <div class="form-group">
                    <label for="calleOrigen">Calle</label>
                    <input type="text" id="calleOrigen" name="calleOrigen" class="form-control"
                      v-model="origin_direction.address" required
                      @input="origin_direction.address = limpiarCaracteresEspeciales(origin_direction.address)"
                      v-b-tooltip.hover.top="'Calle del domicilio (sin caracteres especiales)'">
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label for="numExtOrigen">No. Ext</label>
                      <input type="text" id="numExtOrigen" name="numExtOrigen" class="form-control"
                        v-model="origin_direction.number" required
                        @input="origin_direction.number = limpiarCaracteresEspeciales(origin_direction.number)"
                        v-b-tooltip.hover.top="'Número exterior'">
                    </div>
                    <div class="form-group col-md-6">
                      <label for="numIntOrigen">No. Int</label>
                      <input type="text" id="numIntOrigen" name="numIntOrigen" class="form-control"
                        v-model="origin_direction.intNumber"
                        @input="origin_direction.intNumber = limpiarCaracteresEspeciales(origin_direction.intNumber)"
                        v-b-tooltip.hover.top="'Número interior (opcional)'">
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="referenciaOrigen">Referencia</label>
                    <input type="text" id="referenciaOrigen" name="referenciaOrigen" class="form-control"
                      v-model="origin_direction.reference_of_origin" required maxlength="35"
                      @input="origin_direction.reference_of_origin = limpiarCaracteresEspeciales(origin_direction.reference_of_origin)"
                      v-b-tooltip.hover.top="'Referencia (máx 35 caracteres, sin símbolos raros)'">
                  </div>
                  <div class="form-group">
                    <label for="coloniaOrigen">Colonia</label>
                    <input type="text" name="coloniaOrigen" id="coloniaOrigen" class="form-control"
                      v-model="origin_direction.colony" :required="origin_direction.country_code === 'MX'"
                      @input="origin_direction.colony = limpiarCaracteresEspeciales(origin_direction.colony)"
                      v-b-tooltip.hover.top="'Colonia o barrio'">
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label for="cpOrigen">Código Postal</label>
                      <input type="text" id="cpOrigen" name="cpOrigen" class="form-control"
                        v-model="origin_direction.postal_code" required
                        :maxlength="origin_direction.country_code === 'MX' ? 5 : 12"
                        @input="origin_direction.country_code === 'MX' && (origin_direction.postal_code = origin_direction.postal_code.replace(/\D/g, '').slice(0, 5))"
                        v-b-tooltip.hover.top="origin_direction.country_code === 'MX' ? 'Código postal a 5 dígitos' : 'Código postal'">
                    </div>
                    <div class="form-group col-md-6">
                      <label for="ciudadOrigen">Ciudad</label>
                      <input type="text" id="ciudadOrigen" name="ciudadOrigen" class="form-control"
                        v-model="origin_direction.city" required
                        @input="origin_direction.city = limpiarCaracteresEspeciales(origin_direction.city)"
                        v-b-tooltip.hover.top="'Ciudad o municipio'">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- DESTINO -->
            <div class="col-md-6 mb-4">
              <div class="card shadow-sm border-0 rounded-lg">
                <div class="card-header bg-success text-white">
                  <strong>Destino</strong>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label for="companyDestino">Compañía</label>
                    <input type="text" id="companyDestino" name="companyDestino" class="form-control"
                      v-model="destination_direction.companyName" required
                      @input="destination_direction.companyName = limpiarCaracteresEspeciales(destination_direction.companyName)"
                      v-b-tooltip.hover.top="'Nombre de la empresa que recibe (sin caracteres especiales)'">
                  </div>
                  <div class="form-group">
                    <label for="nombreDestino">Nombre y Apellido</label>
                    <input type="text" name="nombreDestino" id="nombreDestino" class="form-control"
                      v-model="destination_direction.full_name" required
                      @input="destination_direction.full_name = limpiarCaracteresEspeciales(destination_direction.full_name)"
                      v-b-tooltip.hover.top="'Nombre completo (sin caracteres especiales)'">
                  </div>
                  <div class="form-group">
                    <label for="telefonoDestino">Teléfono</label>
                    <input type="tel" name="telefonoDestino" id="telefonoDestino" class="form-control"
                      v-model="destination_direction.phone" required maxlength="10"
                      @input="destination_direction.phone = destination_direction.phone.replace(/\D/g, '').slice(0, 10)"
                      v-b-tooltip.hover.top="'Teléfono de contacto exacto a 10 dígitos'">
                    <small class="text-danger"
                      v-if="destination_direction.phone && destination_direction.phone.length < 10">
                      Debe contener exactamente 10 dígitos.
                    </small>
                  </div>
                  <div class="form-group">
                    <label for="emailDestino">Correo electrónico</label>
                    <input type="email" name="emailDestino" id="emailDestino" class="form-control"
                      v-model="destination_direction.email" required
                      v-b-tooltip.hover.top="'Correo para notificar la entrega'">
                  </div>
                  <div class="form-group">
                    <label for="calleDestino">Calle</label>
                    <input type="text" name="calleDestino" id="calleDestino" class="form-control"
                      v-model="destination_direction.address" required
                      @input="destination_direction.address = limpiarCaracteresEspeciales(destination_direction.address)"
                      v-b-tooltip.hover.top="'Calle del domicilio (sin caracteres especiales)'">
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label for="numExtDestino">No. Ext</label>
                      <input type="text" name="numExtDestino" id="numExtDestino" class="form-control"
                        v-model="destination_direction.number" required
                        @input="destination_direction.number = limpiarCaracteresEspeciales(destination_direction.number)"
                        v-b-tooltip.hover.top="'Número exterior'">
                    </div>
                    <div class="form-group col-md-6">
                      <label for="numIntDestino">No. Int</label>
                      <input type="text" name="numIntDestino" id="numIntDestino" class="form-control"
                        v-model="destination_direction.intNumber"
                        @input="destination_direction.intNumber = limpiarCaracteresEspeciales(destination_direction.intNumber)"
                        v-b-tooltip.hover.top="'Número interior (opcional)'">
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="referenciaDestino">Referencia</label>
                    <input type="text" name="referenciaDestino" id="referenciaDestino" class="form-control"
                      v-model="destination_direction.reference_of_origin" required maxlength="35"
                      @input="destination_direction.reference_of_origin = limpiarCaracteresEspeciales(destination_direction.reference_of_origin)"
                      v-b-tooltip.hover.top="'Referencia (máx 35 caracteres, sin símbolos)'">
                  </div>
                  <div class="form-group">
                    <label for="coloniaDestino">Colonia</label>
                    <input type="text" name="coloniaDestino" id="coloniaDestino" class="form-control"
                      v-model="destination_direction.colony" :required="destination_direction.country_code === 'MX'"
                      @input="destination_direction.colony = limpiarCaracteresEspeciales(destination_direction.colony)"
                      v-b-tooltip.hover.top="'Colonia o barrio'">
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label for="cpDestino">Código Postal</label>
                      <input type="text" name="cpDestino" id="cpDestino" class="form-control"
                        v-model="destination_direction.postal_code" required
                        :maxlength="destination_direction.country_code === 'MX' ? 5 : 12"
                        @input="destination_direction.country_code === 'MX' && (destination_direction.postal_code = destination_direction.postal_code.replace(/\D/g, '').slice(0, 5))"
                        v-b-tooltip.hover.top="destination_direction.country_code === 'MX' ? 'Código postal a 5 dígitos' : 'Código postal'">
                    </div>
                    <div class="form-group col-md-6">
                      <label for="ciudadDestino">Ciudad</label>
                      <input type="text" name="ciudadDestino" id="ciudadDestino" class="form-control"
                        v-model="destination_direction.city" required
                        @input="destination_direction.city = limpiarCaracteresEspeciales(destination_direction.city)"
                        v-b-tooltip.hover.top="'Ciudad o municipio'">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- CONTENIDO DEL ENVÍO -->
          <div class="card shadow-sm border-0 rounded-lg mb-4">
            <div class="card-header bg-light">
              <strong>Contenido del envío</strong>
            </div>
            <div class="card-body">
              <input type="text" class="form-control" v-model="description_of_the_content"
                placeholder="Descripción del contenido" required
                v-b-tooltip.hover.top="'Describe brevemente qué contiene el paquete para facilitar el procesamiento aduanal y logístico'">
            </div>
          </div>
          <hr>
          <div class="form-group">
            <span class="d-flex justify-content-between">
              <span>Correos de notificación <sup>Adicional</sup></span>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="addEmailField(email_notification)"
                v-b-tooltip.hover.top="'Agregar un nuevo correo de notificación'">
                <i class="bi bi-plus-circle"></i> Agregar correo
              </button>
            </span>

            <div v-for="(mail, index) in email_notification" :key="index" class="input-group mb-2">
              <input type="email" class="form-control" v-model="email_notification[index]"
                placeholder="correo@ejemplo.com" required
                v-b-tooltip.hover.top="'Ingresa una dirección de correo para enviar notificaciones del estatus del envío'">
              <div class="input-group-append" v-if="email_notification.length > 1">
                <button class="btn btn-outline-danger" type="button"
                  @click="removeEmailField(index, email_notification)"
                  v-b-tooltip.hover.top="'Eliminar este correo de notificación'">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="form-group">
            <span class="font-weight-bold"> Mensaje personalizado:</span>
            <textarea v-model="selectedService.messageBody" class="form-control" rows="2"
              placeholder="Escribe una descripción para el correo..."
              v-b-tooltip.hover.top="'Este mensaje se incluirá en el correo de notificación que recibirá el destinatario'"></textarea>
          </div>
          <hr>

          <div class="card shadow-sm border-0 rounded-lg mb-4" v-if="selectedService.carrier == 'TRESGUERRAS'">
            <div class="card-header bg-warning text-white">
              <strong>Detalle Complemento Carta Porte (CCP)</strong>
            </div>

            <div class="card-body pt-0" v-for="(item, index) in detalleCCP" :key="index">
              <hr v-if="index > 0">

              <div class="mb-3 d-flex justify-content-between align-items-center">
                <h6 class="mb-0 text-muted">Item de Carga #{{ index + 1 }}</h6>

                <div class="form-check form-switch mb-0" v-if="index > 0">
                  <input class="form-check-input" type="checkbox" :id="'repetirCheck-' + index"
                    v-model="item.repetirPadre" @change="item.repetirPadre ? (
                      item.descripcion = detalleCCP[0].descripcion,
                      item.sat_producto = detalleCCP[0].sat_producto,
                      item.sat_claseBulto = detalleCCP[0].sat_claseBulto,
                      item.sat_embalaje = detalleCCP[0].sat_embalaje,
                      item.sat_material_peligroso = detalleCCP[0].sat_material_peligroso
                    ) : null">
                  <label class="form-check-label text-muted small" :for="'repetirCheck-' + index">
                    ¿Copiar datos del Item #1?
                  </label>
                </div>
              </div>

              <div class="form-group mb-3">
                <label :for="'descripcion-' + index">Descripción Breve *</label>
                <input :id="'descripcion-' + index" type="text" class="form-control" v-model="item.descripcion"
                  placeholder="Descripción del producto (máx 50 carácteres)" required
                  :disabled="index > 0 && item.repetirPadre"
                  v-b-tooltip.hover.top="'Descripción clara de la mercancía para efectos aduanales'">
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label :for="'sat_producto-' + index">Clave Producto SAT *</label>
                  <AutocompleteInput v-model="item.sat_producto" api-url="/api/v1/sat/productos"
                    placeholder="Escribe código o descripción..." maxlength="8" :required="true"
                    @select="handleSelectProducto($event, index)" :disabled="index > 0 && item.repetirPadre"
                    v-b-tooltip.hover.top="'Catálogo c_ClaveProdServCP del SAT'" />
                </div>

                <div class="col-md-6 mb-3">
                  <label :for="'sat_claseBulto-' + index">Clave Clase Bulto SAT *</label>
                  <AutocompleteInput v-model="item.sat_claseBulto" api-url="/api/v1/sat/unidades" placeholder="Ej: 03"
                    maxlength="3" required :disabled="index > 0 && item.repetirPadre"
                    @select="handleSelectBulto($event, index)"
                    v-b-tooltip.hover.top="'Catálogo c_ClaveUnidadPeso del SAT'" />
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label :for="'sat_embalaje-' + index">Clave Embalaje SAT</label>
                  <AutocompleteInput v-model="item.sat_embalaje" api-url="/api/v1/sat/embalajes"
                    placeholder="Escribe código o tipo de embalaje..." maxlength="8"
                    @select="index > 0 && item.repetirPadre ? null : item.sat_embalaje = $event.code"
                    :disabled="index > 0 && item.repetirPadre"
                    v-b-tooltip.hover.top="'Catálogo c_TipoEmbalaje del SAT'" />
                </div>

                <div class="col-md-6 mb-3">
                  <label :for="'sat_material_peligroso-' + index">Material Peligroso</label>
                  <AutocompleteInput v-model="item.sat_material_peligroso" api-url="/api/v1/sat/materiales-peligrosos"
                    placeholder="Escribe código o sustancia..."
                    @select="index > 0 && item.repetirPadre ? null : item.sat_material_peligroso = $event.code"
                    :disabled="index > 0 && item.repetirPadre"
                    v-b-tooltip.hover.top="'Selecciona si la mercancía es material peligroso'" />
                </div>
              </div>
            </div>
          </div>

          <div class="card shadow-sm border-0 rounded-lg mb-4" v-if="isInternational">
            <div class="card-header bg-info text-white">
              <strong>Declaración Aduanal</strong>
            </div>
            <div class="card-body pt-0" v-for="(item, index) in internationalProducts" :key="index">
              <hr v-if="index > 0">
              <div class="mb-3 d-flex justify-content-between align-items-center">
                <h6 class="mb-0 text-muted">Producto #{{ index + 1 }}</h6>
                <button type="button" class="btn btn-sm btn-outline-danger" v-if="internationalProducts.length > 1"
                  @click="internationalProducts.splice(index, 1)">
                  Quitar
                </button>
              </div>
              <div class="form-group mb-3">
                <label :for="'intl-descripcion-' + index">Descripción *</label>
                <input :id="'intl-descripcion-' + index" type="text" class="form-control" v-model="item.description"
                  required maxlength="100" placeholder="Descripción del producto"
                  v-b-tooltip.hover.top="'Descripción del producto declarado para aduana'">
              </div>
              <div class="row">
                <div class="col-md-3 mb-3">
                  <label :for="'intl-cantidad-' + index">Cantidad *</label>
                  <input :id="'intl-cantidad-' + index" type="number" min="1" class="form-control"
                    v-model.number="item.total_units" required>
                </div>
                <div class="col-md-3 mb-3">
                  <label :for="'intl-valor-' + index">Valor Declarado (USD) *</label>
                  <input :id="'intl-valor-' + index" type="number" min="0.01" step="0.01" class="form-control"
                    v-model.number="item.declared_cost" required>
                </div>
                <div class="col-md-3 mb-3">
                  <label :for="'intl-peso-' + index">Peso (kg) *</label>
                  <input :id="'intl-peso-' + index" type="number" min="0.01" step="0.01" class="form-control"
                    v-model.number="item.weight" required>
                </div>
                <div class="col-md-3 mb-3">
                  <label :for="'intl-pais-' + index">País de Manufactura *</label>
                  <select :id="'intl-pais-' + index" class="form-control" v-model="item.manufacture_country" required>
                    <option value="MX">México</option>
                    <option v-for="c in countryOptions" :key="c.code" :value="c.code">{{ c.name }}</option>
                  </select>
                </div>
              </div>
              <div class="form-group mb-3">
                <label :for="'intl-hs-' + index">Fracción Arancelaria / HS Code *</label>
                <input :id="'intl-hs-' + index" type="text" class="form-control" v-model="item.commodity_code"
                  required maxlength="15" placeholder="Ej: 392310 (solo dígitos, 6-10)"
                  v-b-tooltip.hover.top="'Código armonizado (HS Code) del producto — solo dígitos, sin puntos'">
              </div>
            </div>
            <div class="card-body pt-0">
              <button type="button" class="btn btn-sm btn-outline-primary" @click="internationalProducts.push({
                description: '', total_units: 1, declared_cost: 0, weight: 0, manufacture_country: 'MX', commodity_code: ''
              })">
                <i class="bi bi-plus-circle mr-1"></i> Agregar producto
              </button>
            </div>
          </div>

          <!-- BOTONES -->
          <div class="text-right">
            <button type="button" class="btn btn-outline-secondary mr-2" @click="prevStep"
              v-b-tooltip.hover.top="'Volver al paso anterior'">
              Anterior
            </button>
            <button type="submit" class="btn btn-primary" v-b-tooltip.hover.top="'Continuar con el siguiente paso'">
              Siguiente
            </button>
          </div>
        </form>
      </div>
      <div class="container ew-step-col" v-if="step == 4">
        <div class="card shadow-sm border-0">
          <div class="card-header bg-white d-flex align-items-center">
            <img :src="shipments.selected_service.carrier_logo_url_svg" alt="Logo" height="40" class="mr-2">
            <h5 class="mb-0 font-weight-bold">{{ shipments.selected_service.carrier }} - {{
              shipments.selected_service.carrier_service_name
              }}</h5>
          </div>
          <div class="card-body">
            <h6 class="font-weight-bold mb-3">Detalles del envío</h6>
            <p><i class="bi bi-geo-alt-fill text-primary mr-2"></i><strong>Origen:</strong> {{
              shipments.origin_direction.full_name }}, {{
              shipments.origin_direction.address
              }}, {{ shipments.origin_direction.city }}</p>
            <p><i class="bi bi-geo-alt-fill text-danger mr-2"></i><strong>Destino:</strong> {{
              shipments.destination_direction.full_name }}, {{
              shipments.destination_direction.address }}, {{ shipments.destination_direction.city }}</p>

            <p><i class="bi bi-box-seam mr-2"></i><strong>Dimensiones:</strong>
              <template v-for="(parcel, index) in shipments.parcels">
                {{ parcel.depth }} x {{ parcel.width }} x {{ parcel.height }} cm{{ index < shipments.parcels.length - 1
                  ? ', ' : '' }} </template>
            </p>

            <p><i class="bi bi-speedometer2 mr-2"></i><strong>Peso:</strong>
              <template v-for="(parcel, index) in shipments.parcels">
                {{ parcel.weight }} kg{{ index < shipments.parcels.length - 1 ? ' + ' : '' }} </template>
            </p>

            <p v-if="!hasRole(['user'])" class="h5 mt-3"><strong>Total a pagar:</strong> {{
              shipments.total_guia | money(shipments.selected_service.currency || 'MXN') }}</p>
            <hr>

            <h5 class="font-weight-bold">Opciones de pago seguro</h5>
            <div class="btn-group btn-group-toggle d-flex" data-toggle="buttons">
              <label class="btn btn-outline-primary" :class="{ active: paymentMethod === 'paypal' }"
                @click="paymentMethod = 'paypal'" v-if="!hasRole(['user'])"
                v-b-tooltip.hover.top="'Pagar de forma segura con PayPal'">
                <input type="radio" name="payment" autocomplete="off"> PayPal
              </label>
              <label class="btn btn-outline-primary" :class="{ active: paymentMethod === 'wallet' }"
                @click="paymentMethod = 'wallet'" v-if="!hasRole(['user']) && total_monedero > 0"
                v-b-tooltip.hover.top="'Pagar con el saldo de tu monedero'">
                <input type="radio" name="payment" autocomplete="off"> <i class="bi bi-wallet2 mr-1"></i>Monedero
                <strong>
                  <span v-if="!hasRole(['user'])">
                    {{ total_monedero | money }}
                  </span>
                </strong>
              </label>
              <label class="btn btn-outline-primary" :class="{ active: paymentMethod === 'card', disabled: !peiixEnabled }"
                @click="peiixEnabled ? paymentMethod = 'card' : null" v-if="!hasRole(['user'])"
                v-b-tooltip.hover.top="peiixEnabled ? 'Pagar con tarjeta de crédito o débito' : 'Próximamente disponible'">
                <input type="radio" name="payment" autocomplete="off" :disabled="!peiixEnabled"> <i
                  class="bi bi-credit-card-fill mr-1"></i>Tarjeta
              </label>
            </div>
            <div class=" container my-4 w-50" v-if="paymentMethod === 'paypal'">
              <div class="card shadow-sm">
                <div class="card-header bg-primary text-white text-center py-2">
                  <h6 class="mb-0">Paga con tarjeta (PayPal)</h6>
                </div>
                <div class="card-body">
                  <p class="text-muted text-center mb-3">
                    Ingresa los datos de tu tarjeta de manera segura con PayPal
                  </p>
                  <div id="paypal-button-container"></div>
                </div>
                <div class="card-footer text-center text-muted small">
                  <i class="fas fa-lock"></i> Pago seguro con PayPal
                </div>
              </div>
            </div>

            <div class="container my-4 w-50" v-if="paymentMethod === 'card'">
              <div class="card shadow-sm">
                <div class="card-header bg-primary text-white py-2 d-flex align-items-center justify-content-between">
                  <h6 class="mb-0"><i class="bi bi-credit-card mr-1"></i>Paga con tarjeta</h6>
                  <div class="d-flex align-items-center">
                    <img :src="visaLogo" alt="Visa" style="height: 18px;" class="mr-2 bg-white rounded p-1">
                    <img :src="mastercardLogo" alt="Mastercard" style="height: 22px;" class="bg-white rounded p-1">
                  </div>
                </div>
                <div class="card-body">
                  <div class="form-group">
                    <label>Número de tarjeta</label>
                    <input type="text" class="form-control" :value="peiixCardDisplay" @input="onPanInput"
                      inputmode="numeric" autocomplete="cc-number" maxlength="19"
                      placeholder="0000 0000 0000 0000">
                  </div>
                  <div class="form-row">
                    <div class="form-group col-6">
                      <label>Vencimiento</label>
                      <div class="d-flex">
                        <select class="form-control mr-1" v-model="peiixCard.expMonth">
                          <option value="" disabled>MM</option>
                          <option v-for="m in expMonthOptions" :key="m" :value="m">{{ m }}</option>
                        </select>
                        <select class="form-control" v-model="peiixCard.expYear">
                          <option value="" disabled>AAAA</option>
                          <option v-for="y in expYearOptions" :key="y" :value="y">{{ y }}</option>
                        </select>
                      </div>
                    </div>
                    <div class="form-group col-2">
                      <label>CVV</label>
                      <input type="password" class="form-control" v-model="peiixCard.cvv"
                        @input="peiixCard.cvv = peiixCard.cvv.replace(/\D/g, '').slice(0, 4)" inputmode="numeric"
                        autocomplete="cc-csc" maxlength="4" placeholder="123">
                    </div>
                    <div class="form-group col-4">
                      <label>Titular</label>
                      <input type="text" class="form-control" v-model="peiixCard.holderName"
                        autocomplete="cc-name" placeholder="Como en la tarjeta">
                    </div>
                  </div>
                  <small v-if="peiixCard.expMonth && peiixCard.expYear && isCardExpired"
                    class="text-danger d-block mb-2">
                    <i class="bi bi-exclamation-circle mr-1"></i>La fecha de vencimiento ya pasó
                  </small>
                  <b-alert v-if="peiixError" variant="danger" show>{{ peiixError }}</b-alert>
                </div>
                <div class="card-footer text-center text-muted small">
                  <i class="bi bi-shield-lock-fill mr-1"></i> Tu pago seguro a través de Peiix
                </div>
              </div>
            </div>

            <div class="mt-3 d-flex justify-content-center">
              <button type="button" class="btn btn-outline-secondary mr-2" @click="prevStep"
                v-b-tooltip.hover.top="'Volver al paso anterior'">Anterior</button>
              <button class="btn btn-primary" v-on:click="paymentMonedero" v-if="paymentMethod === 'wallet'"
                v-b-tooltip.hover.top="'Pagar con tu monedero'">Pagar con
                {{ paymentMethod
                }}</button>
              <button class="btn btn-primary" v-on:click="openPeiixConfirm" v-if="paymentMethod === 'card'"
                :disabled="peiixLoading || !peiixCardValid" v-b-tooltip.hover.top="'Pagar con tarjeta'">
                {{ peiixLoading ? 'Procesando...' : 'Pagar con tarjeta' }}
              </button>
            </div>
          </div>
        </div>

        <b-modal id="peiix-confirm-modal" v-model="showPeiixConfirmModal" title="Confirmar pago" hide-footer>
          <p>Estás a punto de pagar <strong>${{ parseFloat(shipments.total_guia) }} {{
            shipments.selected_service.currency || 'MX' }}</strong>
            con la tarjeta terminación <strong>{{ peiixCard.pan.slice(-4) }}</strong>.</p>
          <div class="text-right mt-3">
            <button class="btn btn-outline-secondary mr-2" @click="showPeiixConfirmModal = false"
              :disabled="peiixLoading">Cancelar</button>
            <button class="btn btn-primary" @click="confirmPeiixPayment" :disabled="peiixLoading">
              {{ peiixLoading ? 'Procesando...' : 'Confirmar pago' }}
            </button>
          </div>
        </b-modal>
      </div>
    </main>
  </div>
</template>


<script>
import axios from 'axios';
import commons from '../commons';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import Navbar from './Header/Navbar';
import { roleMixin } from '@/mixins/roleMixin';
import AutocompleteInput from './SAT/AutocompleteInput.vue';
import CountrySelect from './CountrySelect.vue';
import SwitchToggle from './SwitchToggle.vue';
import visaLogo from '../assets/images/visa.png';
import mastercardLogo from '../assets/images/mastercard.png';
import { countryOptions } from '../utils/countries';
import { getRegionsForCountry } from '../utils/countryRegions';

export default {
  name: 'NewShipmentFlow',

  mixins: [roleMixin], directives: {
    'click-outside': {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
          if (!(el == event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    }
  },
  data() {
    return {
      step: 1,
      wizardSteps: [
        { id: 1, label: 'Origen y Destino' },
        { id: 2, label: 'Comparar Tarifas' },
        { id: 3, label: 'Detalles y Aduana' },
        { id: 4, label: 'Pago' },
      ],
      newProfile: {
        name: '',
        type: '',
        dimensions: { length: null, width: null, height: null },
        weight: null,
        description: ''
      },
      newAddress: {
        company: '',
        name: '',
        phone: '',
        email: '',
        address: '',
        number: '',
        intNumber: '',
        colony: '',
        reference_of_origin: '',
        origin: '',
        postal_code: '',
        city: '',
        country_code: 'MX',
      },
      originAddress: {
        flag: "https://restcountries.eu/data/mex.svg",
        country_code: 'MX',
        input: ''
      },

      isLoadingAddress: false,
      isOpenAddress: false,
      packageProfiles: [],      // todos los perfiles del usuario
      selectedProfile: "",      // ID del perfil seleccionado
      paymentMethod: '', // paypal, card, wallet
      cantidadGuias: 1,
      shipments: [],
      isLoadingPage: false,
      selectedOrigen: '',
      selectedDestino: '',
      origen: '',
      destino: '',
      tipoEnvio: 'package',
      parcels: [
        { peso: 0, largo: 0, ancho: 0, alto: 0 }
      ], description_of_the_content: '',
      valorDeclarado: 0,
      optionsOrigen: [],
      optionsPickup: [],
      origenPostal: '',
      optionsDestination: [],
      destinoPostal: '',
      idUser: null,
      isLoging: false,
      origin: {
        flag: "https://restcountries.eu/data/mex.svg",
        country_code: 'MX',
      },
      destination: {
        flag: "https://restcountries.eu/data/mex.svg",
        country_code: 'MX',
      },
      selectedService: {},
      origin_direction: {
        companyName: '',
        full_name: '',
        email: '',
        phone: '',
        address: '',
        number: '',
        intNumber: '',
        reference_of_origin: '',
        colony: '',
        postal_code: '',
        state_short: '',
        state: '',
        city: '',
        country_code: 'MX',
        emails: []
      },
      destination_direction: {
        companyName: '',
        full_name: '',
        email: '',
        phone: '',
        address: '',
        number: '',
        intNumber: '',
        reference_of_origin: '',
        colony: '',
        postal_code: '',
        state_short: '',
        state: '',
        city: '',
        country_code: 'MX',
        emails: []

      },
      bandera_recoleccion: 'S',
      bandera_ead: 'S',
      bandera_condicion_pago: 'CR',
      detalleCCP: [{ descripcion: '', sat_producto: '', sat_claseBulto: '', sat_embalaje: '', sat_material_peligroso: '' }
      ],
      countryOptions: countryOptions,
      internationalProducts: [
        { description: '', total_units: 1, declared_cost: 0, weight: 0, manufacture_country: 'MX', commodity_code: '' }
      ],
      user: {
        first_names: "",
        last_names: "",
        email: "",
        active: false
      },
      email_notification: [],
      //Para el codigo postal de origen
      firstLoading: true,
      isOpen: false,
      results: [],
      scroll: 0,
      isLoading: false,
      arrowCounter: -1,
      //Para el codigo postal de destino
      isOpenDes: false,
      resultsDes: [],
      scrollDes: 0,
      isLoadingDes: false,
      arrowCounterDes: -1,
      monederoDisabled: false,
      peiixEnabled: true,
      peiixCard: { pan: '', expMonth: '', expYear: '', cvv: '', holderName: '' },
      peiixLoading: false,
      peiixError: '',
      showPeiixConfirmModal: false,
      visaLogo,
      mastercardLogo,
      netPrice: 0,
      total: 0,
      overweight: 0,
      cost_overweight: 0,
      total_monedero: 0,
      //inifite Origen
      locations: [],
      currentPage: 1,
      totalPages: 1,
      searchKeywordDestination: '',
      searchKeywordOrigin: '',
      searchKeywordPickup: '',
      timeout: null,
      showDropdown: false,
      sortBy: 'total_amount',
      sortOrder: 'asc',
      //Modal de recolección
      hasCustomPickup: false,
      pickupDate: new Date().toISOString().split('T')[0],
      readyByTime: '09:00',
      closeTime: '18:00',
      showDropdownDestination: false,
      showDropdownPickup: false,
      pickup_direction_id: null,
      tempService: null,
      selectedPickupDetail: {},
      carriersWithoutCustomPickup: ['dhl', 'estafeta', 'paquete', 'redpack', 'bigsmart', 'afimex'],
      selectedRate: null,
      repetirMismaMercancia: false, // Controla el checkbox
      deliveryType: 'domicilio', // 'domicilio' por defecto
      originAddressMode: 'saved', // 'saved' | 'new' — pestaña activa del bloque Origen
      destinationAddressMode: 'new', // 'saved' | 'new' — pestaña activa del bloque Destino
      originAddressArrow: -1, // índice resaltado por teclado en el combo de direcciones guardadas
      destinationAddressArrow: -1,
      originAddressSelected: false, // true cuando se eligió una dirección guardada de origen
      destinationAddressSelected: false,
      addressModeOptions: [
        { value: 'saved', label: 'Dirección guardada', icon: 'bi-bookmark-fill' },
        { value: 'new', label: 'Nueva dirección', icon: 'bi-plus-lg' },
      ],
      deliveryTypeOptions: [
        { value: 'domicilio', label: 'A Domicilio', icon: 'bi-house-door' },
        { value: 'ocurre', label: 'A Ocurre (Sucursal)', icon: 'bi-building' },
      ],
      selectedOcurreText: '',
      filtroCiudad: '',
      ocurresCatalog: [
      ],
      searchTimeout: null
    };
  },
  filters: {
    formatPrice(value) {
      return value ? parseFloat(value).toFixed(2) : '0.00';
    }
  },
  created() {
    document.addEventListener("click", this.handleClickOutside);
    this.getStatusSesion();
    if (localStorage.getItem('ew-user-id') !== null) {
      axios
        .get(`${commons.apiURL}/api/v1/users/${localStorage.getItem('ew-user-id')}`)
        .then(response => {
          this.user.email = response.data.email
          this.user.first_names = response.data.firstName
          this.user.last_names = response.data.lastName
          this.user.active = response.data.active
          console.log(this.user.email);
          localStorage.setItem('ew-user-role', response.data.rol);
        }).catch(error => {
          console.trace(error)
          alert(error);
        });
    } else {
      console.log('Sin sessión')
      alert('Sin sessión')
    }
  },
  watch: {
    isInternational(newVal) {
      // Envíos internacionales: por ahora solo se acepta 1 paquete por guía
      // (evita declarar el mismo valor de mercancía duplicado en varias guías).
      if (newVal && this.parcels.length > 1) {
        this.parcels = [this.parcels[0]];
      }
      // Precarga el peso del primer producto declarado con el peso del
      // paquete ya capturado en el Paso 1 — evita el caso más común de
      // "peso de mercancía mayor al peso del paquete" (FedEx lo rechaza:
      // COMMODITYWEIGHT.GREATERTHAN.PACKAGEWEIGHT). Solo si el usuario aún
      // no lo había tocado (sigue en el valor por default) y solo para el
      // primer producto — el declarado no tiene una fuente equivalente en
      // el Paso 1, así que ese se deja en blanco a propósito.
      if (newVal && this.internationalProducts.length === 1 && !this.internationalProducts[0].weight) {
        this.internationalProducts[0].weight = Number(this.parcels[0] && this.parcels[0].peso) || 0;
      }
    },
    isLoadingPage(loading) {
      if (loading) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    step() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    'detalleCCP.0': {
      handler(newVal) {
        if (this.repetirMismaMercancia && newVal) {
          this.replicarInformacionSAT();
        }
      },
      deep: true
    },
    repetirMismaMercancia(activo) {
      if (activo && this.detalleCCP.length > 1) {
        this.replicarInformacionSAT();
      }
    },
    parcels: {
      handler(newVal) {
        this.cantidadGuias = newVal.length;
      },
      immediate: true,
      deep: false
    },
    paymentMethod(newVal) {
      if (newVal === 'paypal') {
        this.$nextTick(() => {
          this.initPaypalPayment();
        });
      }
    }
  },
  methods: {
    async fetchOcurres() {
      // Si el campo está vacío, limpiamos la lista y salimos
      if (!this.filtroCiudad || this.filtroCiudad.trim() === '') {
        this.ocurresCatalog = [];
        return;
      }

      try {
        const response = await axios.get(`${commons.apiURL}/api/v1/locations/search?query=${this.filtroCiudad}`);
        this.ocurresCatalog = response.data.data;
      } catch (error) {
        console.error("Error al buscar sucursales:", error);
      }
    },

    // Método con debounce para dispararlo en el input sin saturar el servidor
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchOcurres();
      }, 400);
    },
    setDeliveryType(type) {
      this.deliveryType = type;
      // bandera_ead es el campo que TRESGUERRAS realmente usa para cotizar
      // a domicilio ('S') o a ocurre ('N') — antes solo se podía cambiar
      // desde un select aparte, sin relación con este selector principal.
      if (type === 'domicilio') {
        this.bandera_ead = 'S';
        this.selectedOcurreText = '';
        this.destination_direction = {
          companyName: '',
          full_name: '',
          email: '',
          phone: '',
          address: '',
          colony: '',
          state_short: '',
          state: '',
          postal_code: '',
          city: '',
          country_code: 'MX'
        };
        console.log('Cambiado a Domicilio: Objeto destination_direction limpiado.');
      } else if (type === 'ocurre') {
        this.bandera_ead = 'N';
      }
    },

    openOcurreModal() {
      this.$bvModal.show('modal-ocurres');
    },
    selectOcurreBranch(ocurre) {
      this.selectedOcurreText = `${ocurre.companyName} | ${ocurre.full_name} | ${ocurre.postal_code} - ${ocurre.colony}, ${ocurre.city}`;
      this.destination_direction.companyName = ocurre.companyName;
      this.destination_direction.full_name = ocurre.full_name;
      this.destination_direction.address = ocurre.address;
      this.destination_direction.colony = ocurre.colony;
      this.destination_direction.postal_code = ocurre.postal_code;
      this.destination_direction.city = ocurre.city;
      this.destination_direction.state = ocurre.state;
      this.destination_direction.state_short = this.shortStateName(ocurre.state);
      this.destination_direction.country_code = ocurre.country_code || 'MX';

      this.$bvModal.hide('modal-ocurres');
      console.log('destination_direction lista para Mongoose:', this.destination_direction);
    },
    limpiarCaracteresEspeciales(texto) {
      if (!texto) return '';
      return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s.,#-]/g, '');
    },
    replicarInformacionSAT() {
      const primerItem = this.detalleCCP[0];

      // Iteramos a partir del segundo elemento (index 1)
      for (let i = 1; i < this.detalleCCP.length; i++) {
        this.detalleCCP[i].descripcion = primerItem.descripcion;
        this.detalleCCP[i].sat_producto = primerItem.sat_producto;
        this.detalleCCP[i].sat_claseBulto = primerItem.sat_claseBulto;
        this.detalleCCP[i].sat_embalaje = primerItem.sat_embalaje;
        this.detalleCCP[i].sat_material_peligroso = primerItem.sat_material_peligroso;
      }
    },
    handleSelectBulto(seleccion, index) {
      this.detalleCCP[index].sat_claseBulto = seleccion.code;
      if (index === 0 && this.repetirMismaMercancia) {
        this.replicarInformacionSAT();
      }
    },
    handleSelectProducto(seleccion, index) {
      this.detalleCCP[index].sat_producto = seleccion.code;

      const matPeligroso = seleccion.raw["Material Peligroso"];
      if (matPeligroso === "1") {
        this.detalleCCP[index].sat_material_peligroso = "MP01";
      } else {
        this.detalleCCP[index].sat_material_peligroso = "";
      }

      if (index === 0 && this.repetirMismaMercancia) {
        this.replicarInformacionSAT();
      }
    },
    copiarDatosOriginales(index) {
      if (this.parcels[index].copiarOriginal) {
        this.parcels[index].peso = this.parcels[0].peso;
        this.parcels[index].largo = this.parcels[0].largo;
        this.parcels[index].ancho = this.parcels[0].ancho;
        this.parcels[index].alto = this.parcels[0].alto;
      }
    },
    addParcel() {
      this.parcels.push({
        peso: null,
        largo: null,
        ancho: null,
        alto: null,
        copiarOriginal: false
      });
    },
    removeParcel(index) {
      if (this.parcels.length > 1) {
        this.parcels.splice(index, 1);
      }
    },
    abrirDesglose(rate) {
      this.selectedRate = rate;
      this.$refs.desgloseModal.show();
    },
    canNotHasCustomPickup(carrierName) {
      if (!carrierName) return true; // Si no se proporciona un nombre de transportista, asumimos que sí puede tener recolección personalizada
      return this.carriersWithoutCustomPickup.includes(carrierName.toLowerCase());
    },
    changeSort(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortOrder = 'asc';
      }
    },

    getSortIcon(field) {
      if (this.sortBy !== field) return 'bi bi-arrow-down-up text-muted small';
      return this.sortOrder === 'asc' ? 'bi bi-sort-up text-primary' : 'bi bi-sort-down text-primary';
    },
    addEmailField(emails) {
      emails.push('');
    },
    removeEmailField(index, emails) {
      if (emails.length > 1) {
        emails.splice(index, 1);
      }
    },
    initializeDetalleCCP() {
      // Si el arreglo ya tiene la longitud correcta, no hacemos nada para evitar perder datos ya introducidos.
      if (this.detalleCCP.length === this.cantidadGuias) {
        return;
      }

      const newDetalleCCP = [];
      for (let i = 0; i < this.cantidadGuias; i++) {
        // Si ya existe un objeto en esta posición, lo reutilizamos para no borrar datos.
        const existingItem = this.detalleCCP[i] || {};

        newDetalleCCP.push({
          descripcion: existingItem.descripcion || '',
          sat_claseBulto: existingItem.sat_claseBulto || '',
          sat_producto: existingItem.sat_producto || '',
          sat_material_peligroso: existingItem.sat_material_peligroso || '',
          sat_embalaje: existingItem.sat_embalaje || ''
        });
      }
      this.detalleCCP = newDetalleCCP;
    },
    showAddProfileModal() {
      this.resetProfileForm();
      this.$refs.addProfile.show();
    },
    hideAddProfileModal() {
      this.$refs.addProfile.hide();
    },
    resetProfileForm() {
      this.newProfile = {
        name: '',
        type: '',
        dimensions: { length: null, width: null, height: null },
        weight: null,
        description: ''
      };
    },
    showAddAddressModal() {
      this.resetAddressForm();
      this.$refs.addAddress.show();
    },
    hideAddAddressModal() {
      this.$refs.addAddress.hide();
    },
    resetAddressForm() {
      this.newAddress = {
        company: '',
        name: '',
        phone: '',
        email: '',
        address: '',
        number: '',
        intNumber: '',
        colony: '',
        reference_of_origin: '',
        origin: '',
        postal_code: '',
        city: '',
        country_code: 'MX',
      };
    },
    checkAddress(event) {
      // console.log(event)
      const query = event.target.value
      if (query.length >= 3 && query.length <= 7) {
        this.getLocationInfo(query, this.newAddress.country_code || 'MX').then(response => {

          this.results = response.data.country_codes
          if (this.results.length > 0) {
            this.isOpenAddress = true
            this.isLoadingAddress = true
          } else {
            this.isOpenAddress = false
            this.isLoadingAddress = false
          }
        })
      }
    },
    setResultAddress(result) {
      this.originAddress.input = result.postal_code;
      this.newAddress.postal_code = result.postal_code
      this.newAddress.colony = result.place_name
      this.newAddress.city = result.city
      this.isOpenAddress = false;
    },
    async saveAddress() {
      const isMX = this.newAddress.country_code === 'MX';
      if (
        !this.newAddress.company ||
        !this.newAddress.name ||
        !this.newAddress.phone ||
        !this.newAddress.email ||
        !this.newAddress.address ||
        !this.newAddress.number ||
        (isMX && (!this.originAddress.input || !this.newAddress.colony)) ||
        (!isMX && !this.newAddress.postal_code) ||
        !this.newAddress.city ||
        !this.newAddress.country_code
      ) {
        this.$bvToast.toast("Completa todos los campos obligatorios", {
          title: "Campos incompletos",
          variant: "warning",
          solid: true
        });
        return;
      }


      try {
        const userID = localStorage.getItem("ew-user-id");
        const payload = {
          company: this.newAddress.company,
          name: this.newAddress.name,
          email: this.newAddress.email,
          phone: this.newAddress.phone,
          address: this.newAddress.address + ', ' + this.newAddress.number + (this.newAddress.intNumber.length > 0 ? ', ' + this.newAddress.intNumber : ''),
          colony: this.newAddress.colony,
          reference_of_origin: this.newAddress.reference_of_origin,
          postal_code: this.newAddress.postal_code,
          city: this.newAddress.city,
          user: userID,
          country_code: this.newAddress.country_code
        };


        const data = await axios.post(`${commons.apiURL}/api/v1/locations-user`, payload);
        this.hideAddAddressModal();
        this.isOpenAddress = false
        this.isLoadingAddress = false
      } catch (error) {
        console.error(error);
        this.$bvToast.toast("Error al guardar el perfil", {
          title: "Error",
          variant: "danger",
          solid: true
        });
      }
    },
    closeDropdown() {
      this.showDropdown = false;
    },
    closeDropdownDestination() {
      this.showDropdownDestination = false;
    },
    handleSelectPickup(item) {
      console.log("Item seleccionado:", item);
      this.pickup_direction_id = item.id;
      this.searchKeywordPickup = '';
      this.selectedPickupDetail = item.raw;
      this.showDropdownPickup = false;
    },
    handleSelectOrigin(item) {
      console.log("Item seleccionado:", item);
      this.searchKeywordOrigin = item.text;
      this.selectLocation(item.raw);
      this.onSearch('');
      this.showDropdown = false;
      this.originAddressSelected = true;
    },
    handleSelectDestination(item) {
      console.log("Item seleccionado:", item);
      this.searchKeywordDestination = item.text;
      this.selectLocationDes(item.raw);
      this.onSearch('');
      this.showDropdownDestination = false;
      this.destinationAddressSelected = true;
    },
    clearOriginSelectedAddress() {
      this.originAddressSelected = false;
      this.searchKeywordOrigin = '';
    },
    clearDestinationSelectedAddress() {
      this.destinationAddressSelected = false;
      this.searchKeywordDestination = '';
    },
    moveOriginAddressArrow(delta) {
      if (!this.optionsOrigen.length) return;
      const max = this.optionsOrigen.length - 1;
      this.originAddressArrow = Math.min(max, Math.max(0, this.originAddressArrow + delta));
    },
    selectOriginAddressAtArrow() {
      const item = this.optionsOrigen[this.originAddressArrow];
      if (item) this.handleSelectOrigin(item);
    },
    moveDestinationAddressArrow(delta) {
      if (!this.optionsDestination.length) return;
      const max = this.optionsDestination.length - 1;
      this.destinationAddressArrow = Math.min(max, Math.max(0, this.destinationAddressArrow + delta));
    },
    selectDestinationAddressAtArrow() {
      const item = this.optionsDestination[this.destinationAddressArrow];
      if (item) this.handleSelectDestination(item);
    },
    debounceSearchPickup() {
      this.showDropdownPickup = true;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.onSearch(this.searchKeywordPickup);
      }, 500);
    },
    debounceSearch() {
      this.showDropdown = true;
      this.originAddressSelected = false;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.onSearch(this.searchKeywordOrigin);
      }, 500);
    },
    debounceSearchDestination() {
      this.showDropdownDestination = true;
      this.destinationAddressSelected = false;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.onSearch(this.searchKeywordDestination);
      }, 500);
    },
    onSearch(searchKeyword) {
      this.currentPage = 1;
      this.loadAddress(1, searchKeyword);
    },
    onScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;

      if (scrollTop + clientHeight >= scrollHeight - 5) {
        if (!this.isLoading && this.currentPage < this.totalPages) {
          this.loadAddress(this.currentPage + 1);
        }
      }
    },
    loadAddress(page = 1, searchKeyword = '') {
      if (this.isLoading) return;

      this.isLoading = true;
      const userId = localStorage.getItem('ew-user-id');
      const role = localStorage.getItem('ew-user-role');

      axios
        .get(`${commons.apiURL}/api/v1/locations-user`, {
          params: {
            user: userId,
            page: page,
            limit: 50,
            name: searchKeyword, rol: role,
          }
        })
        .then(response => {
          const newDocs = response.data.docs;
          this.totalPages = response.data.pages;
          this.currentPage = response.data.page;

          if (page === 1 & this.firstLoading) {
            const defaultAddress = newDocs.find(loc => loc.isDefault === true);
            if (defaultAddress) {
              this.selectLocation(defaultAddress); // Asigna los datos a tus campos
              this.searchKeywordOrigin = `${defaultAddress.name} ${defaultAddress.address} ${defaultAddress.colony} C.P. ${defaultAddress.postal_code}`;
              this.originAddressSelected = true;
            }
          }
          this.firstLoading = false;
          const formattedOptions = newDocs.map((loc, index) => ({
            id: loc._id || index,
            text: `${loc.name} ${loc.address} ${loc.colony} C.P. ${loc.postal_code}`,
            raw: loc
          }));

          if (page === 1) {
            this.optionsOrigen = formattedOptions;
            this.optionsDestination = formattedOptions;
            this.optionsPickup = formattedOptions;

          } else {
            this.optionsOrigen = [...this.optionsOrigen, ...formattedOptions];
            this.optionsDestination = [...this.optionsDestination, ...formattedOptions];
            this.optionsPickup = [...this.optionsPickup, ...formattedOptions];

          }
        })
        .catch(error => console.error("Error cargando direcciones:", error))
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Límite de Paquetexpress para sobres (ALTO EXCEDE MAXIMO PERMITIDO DE 1.5 CM)
    envelopeTooThick(alto) {
      const value = Number(alto);
      return alto !== '' && alto !== null && alto !== undefined && !isNaN(value) && value > 1.5;
    },
    async saveProfile() {
      if (this.newProfile.type === 'document' && this.envelopeTooThick(this.newProfile.dimensions.height)) {
        this.$bvToast.toast("Un sobre no puede medir más de 1.5 cm de alto.", {
          title: "Alto no permitido",
          variant: "warning",
          solid: true
        });
        return;
      }
      if (!this.newProfile.name || !this.newProfile.type) {
        this.$bvToast.toast("Completa todos los campos obligatorios", {
          title: "Campos incompletos",
          variant: "warning",
          solid: true
        });
        return;
      }

      try {
        const userID = localStorage.getItem("ew-user-id");
        const payload = {
          ...this.newProfile,
          userID: userID
        };
        const data = await axios.post(`${commons.apiURL}/api/v1/packageProfileRoutes`, payload);
        this.loadPackageProfiles();
        this.hideAddProfileModal();
      } catch (error) {
        console.error(error);
        this.$bvToast.toast("Error al guardar el perfil", {
          title: "Error",
          variant: "danger",
          solid: true
        });
      }
    },
    async loadPackageProfiles() {
      try {
        const userID = localStorage.getItem("ew-user-id");
        const res = await axios.get(`${commons.apiURL}/api/v1/packageProfileRoutes?userID=${userID}`);
        this.packageProfiles = res.data.profiles || [];
      } catch (err) {
        console.error("Error cargando perfiles:", err);
      }
    },

    applyProfile() {
      const profile = this.packageProfiles.find(p => p._id === this.selectedProfile);
      if (profile) {
        let peso = profile.weight;
        let largo = profile.dimensions.length;
        let ancho = profile.dimensions.width;
        let alto = profile.dimensions.height;
        if (profile.description) {
          this.description_of_the_content = profile.description;
        }
        this.parcels = [{ peso, largo, ancho, alto }];
      }
    },
    numberIntegerPositiveCantidadGuias(event) {
      const valorIngresado = Number(event.target.value);

      if (valorIngresado > 0) {
        this.cantidadGuias = Math.ceil(valorIngresado).toString();
      } else {
        this.cantidadGuias = '1';
      }
    },
    // Valida la declaración aduanal contra las reglas reales que ya vimos
    // rechazar en FedEx (peso de mercancía > peso del paquete, valor
    // declarado en $0, HS code sin formato numérico) — se detectan aquí,
    // antes de crear el envío, en vez de hasta el intento real de guía.
    validateInternationalProducts() {
      const packageWeight = Number(this.parcels[0] && this.parcels[0].peso) || 0;
      const totalDeclaredWeight = this.internationalProducts.reduce(
        (sum, p) => sum + (Number(p.weight) || 0), 0
      );
      if (totalDeclaredWeight > packageWeight) {
        this.$bvToast.toast(
          `El peso total declarado en aduana (${totalDeclaredWeight} kg) no puede ser mayor al peso del paquete (${packageWeight} kg).`,
          { title: "Declaración aduanal", variant: "warning", solid: true }
        );
        return false;
      }
      for (const [index, p] of this.internationalProducts.entries()) {
        if (!(Number(p.declared_cost) > 0)) {
          this.$bvToast.toast(
            `El valor declarado del producto #${index + 1} debe ser mayor a $0.`,
            { title: "Declaración aduanal", variant: "warning", solid: true }
          );
          return false;
        }
        const hsDigits = String(p.commodity_code || '').replace(/\D/g, '');
        if (hsDigits.length < 6 || hsDigits.length > 10) {
          this.$bvToast.toast(
            `El código HS del producto #${index + 1} debe tener entre 6 y 10 dígitos (ej. 392310).`,
            { title: "Declaración aduanal", variant: "warning", solid: true }
          );
          return false;
        }
      }
      return true;
    },
    onRegionSelect(field, regions, code) {
      const found = regions.find((r) => r.code === code);
      this[field].state_short = code;
      this[field].state = found ? found.name : code;
    },
    shortStateName(stateFullName) {
      if (!stateFullName) return '';

      const mapping = {
        "Aguascalientes": "AGS",
        "Baja California": "BC",
        "Baja California Sur": "BCS",
        "Campeche": "CAMP",
        "Coahuila de Zaragoza": "COAH",
        "Colima": "COL",
        "Chiapas": "CHIS",
        "Chihuahua": "CHIH",
        "Ciudad de México": "CDMX",
        "Durango": "DGO",
        "Guanajuato": "GTO",
        "Guerrero": "GRO",
        "Hidalgo": "HGO",
        "Jalisco": "JAL",
        "México": "MEX",
        "Michoacán de Ocampo": "MICH",
        "Morelos": "MOR",
        "Nayarit": "NAY",
        "Nuevo León": "NL",
        "Oaxaca": "OAX",
        "Puebla": "PUE",
        "Querétaro": "QRO",
        "Quintana Roo": "QROO",
        "San Luis Potosí": "SLP",
        "Sinaloa": "SIN",
        "Sonora": "SON",
        "Tabasco": "TAB",
        "Tamaulipas": "TAMPS",
        "Tlaxcala": "TLAX",
        "Veracruz de Ignacio de la Llave": "VER",
        "Yucatán": "YUC",
        "Zacatecas": "ZAC"
      };

      // Normaliza acentos y busca coincidencia
      const normalized = stateFullName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const entry = Object.entries(mapping).find(([full]) =>
        full.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === normalized.toLowerCase()
      );

      return entry ? entry[1] : stateFullName;
    },
    setResult(result) {
      this.selectedOrigen = result.postal_code + ' - ' + result.place_name + ' - ' + result.city;
      this.origin_direction.postal_code = result.postal_code;
      this.origin_direction.city = result.city;
      this.origin_direction.colony = result.place_name;
      this.origin_direction.state = result.state;
      this.origin_direction.state_short = this.shortStateName(result.state);
      this.isOpen = false;
      this.isLoading = false;
      console.log('origin_direction', this.origin_direction);
    },
    setResultDes(result) {
      this.selectedDestino = result.postal_code + ' - ' + result.place_name + ' - ' + result.city;
      this.destination_direction.postal_code = result.postal_code;
      this.destination_direction.city = result.city;
      this.destination_direction.colony = result.place_name;
      this.destination_direction.state = result.state;
      this.destination_direction.state_short = this.shortStateName(result.state);
      this.isOpenDes = false;
      this.isLoadingDes = false;
    },
    setSelectedService(val) {
      this.selectedService = val;
      if (val.requires_pickup) {
        this.pickup_direction_id = null;
        //this.hasCustomPickup = !this.canNotHasCustomPickup(val.carrier);// Si el transportista no permite recolección personalizada, forzamos a false para ocultar campos y evitar confusión
        this.hasCustomPickup = false; // Desactivamos temporalmente la opción de recolección personalizada para todos los transportistas mientras se resuelven detalles de implementación
        this.$refs.pickupModal.show();
        return;
      }
      this.continueToNextStep();
    },
    continueToNextStep() {
      this.origin_direction.email = localStorage.getItem('ew-user-email');
      this.messageBody = 'Hola, te comparto los datos de envío de tu paquete.';
      console.log('selectedService with pickup data:', this.selectedService);
      console.log('origin_direction', this.origin_direction);
      console.log('destination_direction', this.destination_direction);
      this.getOverWeight();
      this.getPrepaidsUser();
      this.nextStep();
    },
    confirmPickupAndContinue() {
      if (!this.pickupDate || !this.readyByTime || !this.closeTime || (this.hasCustomPickup && !this.pickup_direction_id)) {
        this.$bvToast.toast("Por favor, completa la fecha, horarios y dirección.", {
          title: "Datos incompletos",
          variant: "warning",
          solid: true
        });
        return;
      }

      const timeData = {
        pickup_date: this.pickupDate,
        ready_by_time: this.readyByTime,
        close_time: this.closeTime
      };

      if (this.hasCustomPickup) {
        this.selectedService.pickup_data = {
          ...this.selectedPickupDetail,
          ...timeData,
          epakData: false
        };
      } else {
        this.selectedService.pickup_data = {
          ...timeData,
          epakData: true
        };
      }

      this.$refs.pickupModal.hide();
      this.continueToNextStep();
    },
    getPrepaidsUser() {
      this.total_monedero = 0;
      this.monederoDisabled = true;
      this.showLowBalanceAlert = false;

      // Obtenemos el ID considerando si usa parentClient o el ID normal
      const userID = localStorage.getItem('ew-user-id') || localStorage.getItem('ew-user-parentClient');


      axios
        .get(`${commons.apiURL}/api/v1/prepaid/getTotalAmount/${userID}`)
        .then((response) => {
          // Parseamos el total asegurando un número válido
          this.total_monedero = parseFloat(response.data.total_amount || 0);

          // Validamos si el saldo cubre el total requerido
          this.monederoDisabled = this.total_monedero < Number(this.total || 0);


          console.log("total monedero: " + this.total_monedero);
          console.log("monederoDisabled: " + this.monederoDisabled);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    formatCurrency(value) {
      return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    },
    getOverWeight() {
      this.overweight = 0;
      this.cost_overweight = 0;
      this.netPrice = this.selectedService.total_amount;
      this.total = this.selectedService.total_amount;
      if (localStorage.getItem("ew-user-id") !== null) {
        console.log(`${commons.apiURL}/api/v1/shipments/all/${localStorage.getItem('ew-user-id')}/overweight`);
        axios
          .get(`${commons.apiURL}/api/v1/shipments/all/${localStorage.getItem('ew-user-id')}/overweight`)
          .then(response => {
            console.log(response.data);
            let shipmentsWithOverweight = response.data;
            for (let i = 0; i < shipmentsWithOverweight.length; i++) {
              if (shipmentsWithOverweight[i].overweight != null && shipmentsWithOverweight[i].cost_overweight != null) {
                this.overweight += parseFloat(shipmentsWithOverweight[i].overweight);
                this.cost_overweight += parseFloat(shipmentsWithOverweight[i].cost_overweight);
              }
            }
            this.total = parseFloat(this.total) + parseFloat(this.cost_overweight);

          })
          .catch(error => {
            console.error(error)
          })
      }
    },
    checkAddressOrigin(event) {
      // console.log(event)
      const query = event.target.value
      if (query.length >= 3 && query.length <= 7) {
        this.getLocationInfo(query, this.origin_direction.country_code || 'MX').then(response => {

          this.results = response.data.country_codes
          if (this.results.length > 0) {
            this.isOpen = true
            this.isLoading = true
          } else {
            this.isOpen = false
            this.isLoading = false
          }
        })
      }
    },
    checkAddressDestination(event) {
      const query = event.target.value
      if (query.length >= 3 && query.length <= 7) {
        this.getLocationInfo(query, this.destination_direction.country_code || 'MX').then(response => {
          this.resultsDes = response.data.country_codes

          if (this.resultsDes.length > 0) {
            this.isOpenDes = true
            this.isLoadingDes = true
          } else {
            this.isOpenDes = false
            this.isLoadingDes = false
          }
        })
      }
    },
    getLocationInfo(zipCode, countryCode) {
      return axios.get(
        `${commons.apiURL
        }/api/v1/postalcodes?search=${zipCode}&country=${countryCode}`
      )
    },
    async getStateShortFromCP(postal_code, countryCode) {
      const locationInfo = await this.getLocationInfo(postal_code, countryCode || 'MX');
      return locationInfo.data.country_codes[0].state;
    },
    async selectLocation(locationOrigin) {

      console.log('locationOrigin origen:', locationOrigin);
      this.selectedOrigen = locationOrigin.postal_code + ' - ' + locationOrigin.address + ' - ' + locationOrigin.city;
      let address = locationOrigin.address.split(',')

      const state = locationOrigin.country_code === 'MX'
        ? await this.getStateShortFromCP(locationOrigin.postal_code, locationOrigin.country_code)
        : (locationOrigin.state_short || '');

      this.origin_direction.companyName = locationOrigin.company
      this.origin_direction.full_name = locationOrigin.name
      this.origin_direction.email = locationOrigin.email
      this.origin_direction.phone = locationOrigin.phone
      this.origin_direction.address = address[0]
      this.origin_direction.number = locationOrigin.address.length > 1 ? address[1] : ''
      this.origin_direction.intNumber = address.length > 2 ? address[2] : ''
      this.origin_direction.reference_of_origin = locationOrigin.reference_of_origin
      this.origin_direction.colony = locationOrigin.colony
      this.origin_direction.postal_code = locationOrigin.postal_code
      this.origin_direction.state_short = this.shortStateName(state)
      this.origin_direction.state = state
      this.origin_direction.city = locationOrigin.city
      this.origin_direction.country_code = locationOrigin.country_code

      console.log('selectLocation', this.origin_direction);
    },
    async selectLocationDes(locationsDes) {
      this.selectedDestino = locationsDes.postal_code + ' - ' + locationsDes.address + ' - ' + locationsDes.city;
      let address = locationsDes.address.split(',');

      const state = locationsDes.country_code === 'MX'
        ? await this.getStateShortFromCP(locationsDes.postal_code, locationsDes.country_code)
        : (locationsDes.state_short || '');

      this.destination_direction.companyName = locationsDes.company
      this.destination_direction.full_name = locationsDes.name
      this.destination_direction.email = locationsDes.email
      this.destination_direction.phone = locationsDes.phone
      this.destination_direction.address = address[0]
      this.destination_direction.number = locationsDes.address.length > 1 ? address[1] : ''
      this.destination_direction.intNumber = address.length > 2 ? address[2] : ''
      this.destination_direction.reference_of_origin = locationsDes.reference_of_origin
      this.destination_direction.colony = locationsDes.colony
      this.destination_direction.postal_code = locationsDes.postal_code
      this.destination_direction.state_short = this.shortStateName(state)
      this.destination_direction.state = state
      this.destination_direction.city = locationsDes.city
      this.destination_direction.country_code = locationsDes.country_code
      console.log(this.destination_direction);
    },
    getStatusSesion() {
      if (localStorage.getItem("ew-user-id") !== null) {
        this.idUser = localStorage.getItem("ew-user-id");
        console.log(this.idUser);
        this.isLoging = true;
      } else {
        this.idUser = null;
        this.isLoging = false;
      }
    },
    async cotizar() {
      try {
        // Campos requeridos
        const requiredFields = {
          paquete: [this.parcels, this.tipoEnvio],
          origen: [
            this.origin_direction.country_code,
            this.origin_direction.postal_code,
            this.origin_direction.city,
            ...(this.origin_direction.country_code === 'MX' ? [this.origin_direction.colony] : [])
          ],
          destino: [
            this.destination_direction.country_code,
            this.destination_direction.postal_code,
            this.destination_direction.city,
            ...(this.destination_direction.country_code === 'MX' ? [this.destination_direction.colony] : [])
          ],
          envio: [this.tipoEnvio],
          usuario: [this.idUser],
          Contenido: [this.description_of_the_content]
        };

        // Validar en loop
        for (let [campo, valores] of Object.entries(requiredFields)) {
          if (valores.some(v => !v)) {
            alert(`Debes completar todos los campos de ${campo}.`);
            return;
          }
        }

        // Las paqueterías rechazan sobres de más de 1.5 cm de alto
        if (this.tipoEnvio === 'document' && this.parcels.some(p => this.envelopeTooThick(p.alto))) {
          alert('Un sobre no puede medir más de 1.5 cm de alto. Corrige el alto o envíalo como Paquete.');
          return;
        }

        this.isLoadingPage = true;
        let requestBody = {
          cantidadGuias: this.cantidadGuias,
          parcels: this.parcels.map(p => ({
            weight: p.peso,
            depth: p.largo,
            width: p.ancho,
            height: p.alto,
          })),
          origin_direction: {
            country_code: this.origin_direction.country_code,
            postal_code: this.origin_direction.postal_code,
            city: this.origin_direction.city,
            suburb: this.origin_direction.colony
          },
          destination_direction: {
            country_code: this.destination_direction.country_code,
            postal_code: this.destination_direction.postal_code,
            city: this.destination_direction.city,
            suburb: this.destination_direction.colony
          },
          package_type: this.tipoEnvio,
          user: this.idUser,
          bandera_recoleccion: this.bandera_recoleccion,
          bandera_ead: this.bandera_ead,
          bandera_condicion_pago: this.bandera_condicion_pago,
          package_insurance: this.valorDeclarado > 0,
          insurance_information: {
            package_value: this.valorDeclarado,
            responsible_for_the_package: this.origin_direction.full_name,
            mail_responsible: this.user.email,
            cost: this.valorDeclarado
          },
        };

        console.log(requestBody);
        this.setQuotingRequestBody(requestBody);
        this.quoting()
          .then(() => this.listAllShippingOptions())
          .catch(() => this.isLoadingPage = false);


      } catch (error) {
        console.log(error);
      }
    },
    createShipment() {
      if (this.isInternational && !this.validateInternationalProducts()) {
        return;
      }
      this.isLoadingPage = true;
      try {
        const applyTrim = (obj, field) => {
          if (obj[field] && typeof obj[field] === 'string') {
            obj[field] = obj[field].trim();
          }
        };

        ['address', 'number', 'intNumber', 'full_name', 'companyName', 'email', 'phone', 'colony'].forEach(field => {
          applyTrim(this.origin_direction, field);
          applyTrim(this.destination_direction, field);
        });
        applyTrim(this, 'description_of_the_content');

        // Respaldo para originalShipment
        const origin_direction = { ...this.origin_direction };
        const destination_direction = { ...this.destination_direction };

        // Concatenación de direcciones
        this.origin_direction.address = `${this.origin_direction.address}, ${this.origin_direction.number}${this.origin_direction.intNumber ? ', ' + this.origin_direction.intNumber : ''}`;
        this.destination_direction.address = `${this.destination_direction.address}, ${this.destination_direction.number}${this.destination_direction.intNumber ? ', ' + this.destination_direction.intNumber : ''}`;

        // Lógica de Parcels (Ahora solo se calcula una vez)
        let finalParcels = this.quotingRequestBody.parcels || [];

        // Determinar tipo de envío
        const shipment_types = { 'tarima': 'tarima', 'document': 'document' };
        const shipment_type = shipment_types[this.quotingRequestBody.package_type] || 'box';

        const cleanEmails = this.email_notification.filter(e => e.trim() !== '');
        const base = parseFloat(this.selectedService.total_amount || 0);
        const sobrepeso = parseFloat(this.cost_overweight || 0);

        // Calculamos solo aquí, localmente
        const totalParaEnvio = Number(((base + sobrepeso)).toFixed(2));
        const requestBody = {
          total_guia: totalParaEnvio,
          selected_service: {
            carrier: this.selectedService.carrier,
            date: this.selectedService.date,
            carrier_logo_url: this.selectedService.carrier_logo_url,
            carrier_logo_url_svg: this.selectedService.carrier_logo_url_svg,
            net_total_amount: this.selectedService.net_total_amount,
            total_amount: parseFloat(this.selectedService.total_amount) + parseFloat(this.cost_overweight),
            estimated_delivery: this.selectedService.estimated_delivery,
            total_transit_days: this.selectedService.total_transit_days,
            carrier_service_name: this.selectedService.carrier_service_name,
            carrier_code: this.selectedService.carrier_code || "",
            currency: this.selectedService.currency
          },
          messageBody: this.selectedService.messageBody,
          email_notification: cleanEmails,
          shipment_type: shipment_type,
          parcels: finalParcels, // Aquí va el array de parcels
          origin_direction: this.origin_direction,
          destination_direction: this.destination_direction,
          description_of_the_content: this.description_of_the_content,
          package_insurance: this.valorDeclarado > 0,
          insurance_information: {
            package_value: this.valorDeclarado,
            responsible_for_the_package: this.origin_direction.full_name,
            mail_responsible: this.user.email,
            cost: this.valorDeclarado
          },
          customer_info: {
            email: this.user.email,
            first_names: this.user.first_names,
            last_names: this.user.last_names
          },
          detalleCCP: this.detalleCCP,
          ...(this.isInternational ? {
            international_products: this.internationalProducts,
            declared_cost: this.internationalProducts.reduce(
              (sum, p) => sum + (Number(p.declared_cost) || 0) * (Number(p.total_units) || 0), 0
            ),
            declared_cost_unit: 'USD',
            shipping_purpose: 'PERSONAL_EFFECTS',
            documents: false,
          } : {}),
          bandera_recoleccion: this.bandera_recoleccion,
          bandera_ead: this.bandera_ead,
          bandera_condicion_pago: this.bandera_condicion_pago,
          user: this.idUser,
          originalShipment: {
            origin_direction: origin_direction,
            destination_direction: destination_direction,
            parcels: finalParcels,
          },
          pickup_data: this.selectedService.pickup_data,
          additionalServices: this.selectedService.additionalServices
        };

        // Enviar petición única
        axios.post(`${commons.apiURL}/api/v1/shipments/home/all`, requestBody)
          .then((response) => {
            this.shipments = response.data.shipments;
            this.nextStep();
          }).catch((error) => {
            console.error(error);
          });

      } finally {
        this.isLoadingPage = false;
      }
    },
    async sendEmails(shipments) {
      const activeEmails = this.email_notification.filter(e => e.includes('@'));
      if (activeEmails.length === 0) {
        // Nadie agregó correos extra de notificación, no hay nada que enviar.
        return;
      }
      console.log('Sending emails to:', shipments);
      try {
        axios
          .post(`${commons.apiURL}/api/v1/send-guide-emails`, {
            shipmentId: shipments._id,
            emails: activeEmails,
            messageBody: shipments.messageBody || 'Hola, te comparto los datos de envío de tu paquete.'
          })
          .then(response => {
            console.log('Emails sent successfully:', response.data);
          })
          .catch(error => {
            console.error(error);
          })
      } catch (e) {
        console.error(e);
      }
    },
    initPaypalPayment() {
      try {
        // Verificar si el SDK de PayPal está cargado
        if (!window.paypal) {
          console.error("El SDK de PayPal aún no se ha cargado.");
          return;
        }
        console.log("El SDK de PayPal aún se ha cargado.");

        // Verificar si el contenedor existe
        const container = document.getElementById("paypal-button-container");
        if (!container) {
          console.error("El contenedor #paypal-button-container no existe en el DOM.");
          return;
        }
        console.log("El contenedor #paypal-button-container existe en el DOM.");

        // Limpiar el contenedor antes de renderizar el botón
        container.innerHTML = "";

        window.paypal
          .Buttons({
            createOrder: async () => {
              try {
                document.querySelectorAll("button.btn-outline-secondary").forEach((elem) => {
                  elem.disabled = true;
                });

                const response = await fetch(
                  `${commons.apiURL}/api/v1/paypal/v2/link/many`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      shipments: this.shipments,
                    }),
                  }
                );

                const orderData = await response.json();

                if (orderData.id) {
                  return orderData.id;
                } else {
                  console.log(orderData);
                  const errorDetail = orderData.details[0];
                  const errorMessage = errorDetail
                    ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
                    : JSON.stringify(orderData);

                  throw new Error(errorMessage);
                }
              } catch (error) {
                this.enablePaymentOptions();
                console.log(`Could not initiate PayPal Checkout...<br><br>${error}`);
              }
            },
            onApprove: async (data, actions) => {
              try {
                const response = await fetch(
                  `${commons.apiURL}/api/v1/paypal/v2/capture-order/${data.orderID}`,
                  {
                    method: "GET",
                  }
                );

                const orderData = await response.json();
                if (orderData.status) {
                  api.shipment
                    .generateGuidesAfterPayMany(this.user.email, this.user, this.shipments)
                    .then(async (response) => {
                      let data = response.data;
                      localStorage.setItem('paymentResponseData', JSON.stringify(data));
                      if (data.execute) {
                        if (this.isRecoleccionLocal) {
                          this.createRecoleccion();
                        }
                        await this.sendEmails(this.shipments);

                        this.$router.push({
                          path: "/payments/result/success",
                          query: { monedero: "true", email: this.user.email }
                        });
                      } else {
                        this.$router.push({ path: "/payments/result/error" });
                      }
                    })
                    .catch((err) => {
                      localStorage.setItem('paymentResponseData', JSON.stringify({
                        message: "no ok",
                        error: err.message || "Error al generar las guías"
                      }));
                      this.$router.push({ path: "/payments/result/error" });
                    });
                  return;
                }
                if (!orderData.details) {
                  throw new Error(JSON.stringify(orderData));
                }
                const errorDetail = orderData.details[0];

                if (errorDetail.issue === "INSTRUMENT_DECLINED") {
                  return actions.restart();
                } else if (errorDetail) {
                  throw new Error(`${errorDetail.description} (${orderData.debug_id})`);
                } else if (!orderData.purchase_units) {
                  throw new Error(JSON.stringify(orderData));
                }
              } catch (error) {
                localStorage.setItem('paymentResponseData', JSON.stringify({
                  message: "no ok",
                  error: error.message || "Error al generar las guías"
                }));
                console.log(`Sorry, your transaction could not be processed...<br><br>${error}`);
                this.$router.push({ path: "/payments/result/error" });
              }
            },
            onCancel: () => {
              this.enablePaymentOptions();
              console.log("Cancel");
            },
            onError: (err) => {
              this.enablePaymentOptions();
              console.log(err);
              localStorage.setItem('paymentResponseData', JSON.stringify({
                message: "no ok",
                error: err.message || "Error al generar las guías"
              }));
              this.$router.push({ path: "/payments/result/error" });
            },
          })
          .render("#paypal-button-container");
      } catch (error) {
        console.log(`Sorry, ${error}`);
      }
    },
    enablePaymentOptions() {
      document.querySelectorAll("button.btn-outline-secondary").forEach((elem) => {
        elem.disabled = false;
      });
    },
    async paymentMonedero() {
      this.isLoadingPage = true;
      try {
        if (this.paymentMethod !== "wallet" || this.monederoDisabled) return;
        this.monederoDisabled = true;
        const response = await axios.post(`${commons.apiURL}/api/v1/payments/monedero/${this.shipments._id}`, {
          email: this.user.email,
          user: localStorage.getItem('ew-user-parentClient'),
          shipments: this.shipments
        });

        localStorage.setItem('paymentErrorData', JSON.stringify(response.data));

        if (response.data.message === "ok") {
          console.log("Pago con monedero exitoso", this.shipments);
          await this.sendEmails(this.shipments);
          this.$router.push({
            path: "/payments/result/success",
            query: { monedero: "true", email: this.user.email }
          });
        } else {
          this.$router.push({
            path: "/payments/result/error",
            query: {
              monedero: "false",
              message: response.data.message,
              error: response.data.error,
              stage: response.data.stage,
              method: "monedero"
            }
          });
        }
      } catch (error) {
        console.error("Error en pago con monedero:", error);
      } finally {
        this.isLoadingPage = false;
      }
    },
    onPanInput(event) {
      this.peiixCard.pan = event.target.value.replace(/\D/g, '').slice(0, 19);
    },
    mapPeiixError(raw) {
      if (!raw) return 'No se pudo procesar el pago. Intenta de nuevo.';
      const msg = raw.toUpperCase();
      if (msg.includes('EXPYEAR') || msg.includes('EXPMONTH') || msg.includes('EXPIRATION')) {
        return 'Revisa la fecha de vencimiento de tu tarjeta.';
      }
      if (msg.includes('CVV')) {
        return 'Revisa el código de seguridad (CVV) de tu tarjeta.';
      }
      if (msg.includes('RECHAZ') || msg.includes('DECLIN') || msg.includes('DENIED')) {
        return 'Tu tarjeta fue rechazada. Intenta con otra tarjeta.';
      }
      if (msg.includes('PAN') || msg.includes('CARD NUMBER') || msg.includes('TARJETA')) {
        return 'Revisa el número de tarjeta ingresado.';
      }
      return 'No se pudo procesar el pago. Verifica tus datos e intenta de nuevo.';
    },
    openPeiixConfirm() {
      if (this.paymentMethod !== "card" || this.peiixLoading || !this.peiixCardValid) return;
      this.peiixError = '';
      this.showPeiixConfirmModal = true;
    },
    async confirmPeiixPayment() {
      if (this.paymentMethod !== "card" || this.peiixLoading) return;
      this.peiixError = '';
      this.peiixLoading = true;
      this.isLoadingPage = true;
      try {
        const response = await axios.post(`${commons.apiURL}/api/v1/payments/peiix/${this.shipments._id}`, {
          email: this.user.email,
          card: this.peiixCard
        });

        localStorage.setItem('paymentErrorData', JSON.stringify(response.data));

        if (response.data.message === "ok") {
          console.log("Pago con tarjeta exitoso", this.shipments);
          this.showPeiixConfirmModal = false;
          await this.sendEmails(this.shipments);
          this.$router.push({
            path: "/payments/result/success",
            query: { card: "true", email: this.user.email }
          });
        } else if (response.data.stage === "guide") {
          // La paquetería rechazó la guía antes de cobrar: no es un problema
          // de la tarjeta, así que no se muestra "tarjeta rechazada".
          this.peiixError = `${response.data.error || 'No se pudo generar la guía.'} No se realizó ningún cargo a tu tarjeta.`;
          this.showPeiixConfirmModal = false;
        } else {
          this.peiixError = this.mapPeiixError(response.data.error);
          this.peiixCard.pan = '';
          this.peiixCard.cvv = '';
          this.showPeiixConfirmModal = false;
        }
      } catch (error) {
        console.error("Error en pago con tarjeta:", error);
        this.peiixError = 'Error al procesar el pago. Intenta de nuevo.';
        this.peiixCard.pan = '';
        this.peiixCard.cvv = '';
        this.showPeiixConfirmModal = false;
      } finally {
        this.peiixLoading = false;
        this.isLoadingPage = false;
      }
    },
    nextStep() {
      if (this.currentStep < 6) {
        this.step++
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.step--
      }
    },
    goToStep(id) {
      // Solo permite regresar a un paso ya completado, no saltar hacia adelante
      // (los pasos siguientes tienen validaciones propias antes de avanzar).
      if (id < this.step) {
        this.step = id;
      }
    },
    listAllShippingOptions() {
      this.nextStep();
      this.isLoadingPage = false;
      this.initializeDetalleCCP();
    },
    ...mapMutations([
      'setQuotingRequestBody',
    ]),
    ...mapActions(['quoting',]),
  },
  destroyed() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  mounted() {
    this.loadPackageProfiles();
    this.loadAddress(1);
  },
  computed: {
    isInternational() {
      return this.origin_direction.country_code !== 'MX' ||
        this.destination_direction.country_code !== 'MX';
    },
    // Subdivisiones ISO 3166-2 (estado/provincia) del país seleccionado.
    // México no trae datos aquí a propósito (sigue su propio flujo de CP);
    // el resto de países, si no tienen subdivisiones catalogadas, cae al
    // input de texto libre como respaldo.
    originRegions() {
      return getRegionsForCountry(this.origin_direction.country_code).map(
        (r) => ({ code: r.code, name: r.name })
      );
    },
    destinationRegions() {
      return getRegionsForCountry(this.destination_direction.country_code).map(
        (r) => ({ code: r.code, name: r.name })
      );
    },
    totalWeightKg() {
      return this.parcels.reduce((sum, p) => sum + (Number(p.peso) || 0), 0);
    },
    tipoEnvioLabel() {
      const labels = { package: 'Caja', document: 'Sobre', tarima: 'Tarima' };
      return labels[this.tipoEnvio] || 'Paquete';
    },
    coveredOptionsCount() {
      return (this.shippingOptions || []).filter(o => o.hasCoverage).length;
    },
    originStreetLine() {
      const parts = [this.origin_direction.address, this.origin_direction.number, this.origin_direction.intNumber]
        .filter(Boolean);
      return parts.join(' ');
    },
    originLocationLine() {
      const parts = [this.origin_direction.colony, this.origin_direction.city, this.origin_direction.state]
        .filter(Boolean);
      let line = parts.join(', ');
      if (this.origin_direction.postal_code) line += (line ? ' · ' : '') + 'CP ' + this.origin_direction.postal_code;
      return line;
    },
    destinationStreetLine() {
      const parts = [this.destination_direction.address, this.destination_direction.number, this.destination_direction.intNumber]
        .filter(Boolean);
      return parts.join(' ');
    },
    destinationLocationLine() {
      const parts = [this.destination_direction.colony, this.destination_direction.city, this.destination_direction.state]
        .filter(Boolean);
      let line = parts.join(', ');
      if (this.destination_direction.postal_code) line += (line ? ' · ' : '') + 'CP ' + this.destination_direction.postal_code;
      return line;
    },
    cheapestOptionPrice() {
      const covered = (this.shippingOptions || []).filter(o => o.hasCoverage && o.total_amount != null);
      if (!covered.length) return null;
      return Math.min(...covered.map(o => Number(o.total_amount))).toFixed(2);
    },
    peiixCardDisplay() {
      return this.peiixCard.pan.replace(/(.{4})/g, '$1 ').trim();
    },
    expMonthOptions() {
      return Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
    },
    expYearOptions() {
      const current = new Date().getFullYear();
      return Array.from({ length: 11 }, (_, i) => String(current + i));
    },
    isCardExpired() {
      if (!this.peiixCard.expMonth || !this.peiixCard.expYear) return false;
      const now = new Date();
      const expiry = new Date(Number(this.peiixCard.expYear), Number(this.peiixCard.expMonth), 0);
      return expiry < new Date(now.getFullYear(), now.getMonth(), 1);
    },
    peiixCardValid() {
      const pan = this.peiixCard.pan;
      return pan.length >= 13 && pan.length <= 19 &&
        !!this.peiixCard.expMonth && !!this.peiixCard.expYear && !this.isCardExpired &&
        this.peiixCard.cvv.length >= 3 && this.peiixCard.cvv.length <= 4 &&
        !!this.peiixCard.holderName.trim();
    },
    sortedShipmentOptions() {
      let options = [...this.shippingOptions];
      console.log('Sorting options by', this.sortBy, this.sortOrder, this.shippingOptions);
      return options.sort((a, b) => {
        let modifier = this.sortOrder === 'desc' ? -1 : 1;
        let fieldA, fieldB;

        switch (this.sortBy) {
          case 'carrier':
            fieldA = a.carrier.toLowerCase();
            fieldB = b.carrier.toLowerCase();
            break;
          case 'time':
            fieldA = a.total_transit_days || new Date(a.estimated_delivery).getTime();
            fieldB = b.total_transit_days || new Date(b.estimated_delivery).getTime();
            break;
          case 'price':
            fieldA = a.total_amount;
            fieldB = b.total_amount;
            break;
          default:
            return 0;
        }

        if (fieldA < fieldB) return -1 * modifier;
        if (fieldA > fieldB) return 1 * modifier;
        return 0;
      });
    },
    // filtrar perfiles por tipo de envío seleccionado
    filteredProfiles() {
      if (!Array.isArray(this.packageProfiles)) return [];
      return this.packageProfiles.filter(p => {
        // Evita errores si p es undefined o no tiene type
        if (!p || !p.type) return false;
        // Filtramos por el tipo de envío seleccionado
        return p.type === this.tipoEnvio;
      });
    },
    currentStep() {
      return this.step
    },
    ...mapGetters([
      'quotingRequestBody',
      'serviceProviders',
      'shippingOptions',
      'isLoadingShippingOptions',
      "ecommerceShipment",
      "countryDes"
    ]),

  },
  components: {
    'navbar-dashboard': Navbar,
    AutocompleteInput,
    CountrySelect,
    SwitchToggle
  }
};
</script>


<style>
.btn-group-toggle .btn {
  flex: 1;
}

.position-relative {
  position: relative;
}

.list-dropdown-container {
  position: absolute;
  top: 100%;
  /* Justo debajo del input */
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 2px;
}

.list-item-option {
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  transition: background 0.2s;
}

.list-item-option:hover {
  background-color: #007bff;
  color: white;
}

/* Sombra suave */
.shadow {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* ---- Stepper + resumen fijo (Cotizador) ---- */
.ew-shipment-main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  grid-template-rows: auto 1fr;
  gap: 18px 28px;
}

.ew-stepper-wrap { grid-column: 1 / -1; grid-row: 1; }

.ew-shipment-main .ew-step-col.container {
  grid-column: 1;
  grid-row: 2;
  max-width: 100%;
  margin: 0 0 1.5rem;
}

.ew-summary-aside {
  grid-column: 2;
  grid-row: 2;
  position: sticky;
  top: 20px;
  align-self: start;
}

@media (max-width: 991px) {
  .ew-shipment-main { grid-template-columns: 1fr; }
  .ew-summary-aside { position: static; grid-column: 1; grid-row: 3; }
}

.ew-stepper { display: flex; margin: 0; padding: 0; }
.ew-stepper__item { flex: 1; position: relative; }

.ew-stepper__item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 17px;
  left: calc(50% + 22px);
  right: calc(-50% + 22px);
  height: 2px;
  background: #e1e3ee;
  z-index: 0;
}

.ew-stepper__item.is-done:not(:last-child)::after { background: #031d7d; }

.ew-stepper__node {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  background: none;
  border: none;
  padding: 4px;
  cursor: default;
}

.ew-stepper__item.is-done .ew-stepper__node { cursor: pointer; }
.ew-stepper__node:focus-visible { outline: 2px solid #dc3545; outline-offset: 2px; border-radius: 8px; }

.ew-stepper__dot {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.85rem;
  border: 2px solid #e1e3ee;
  background: #fff;
  color: #8a8fb3;
  transition: background-color .18s ease, border-color .18s ease, color .18s ease;
}

.ew-stepper__item.is-active .ew-stepper__dot { border-color: #031d7d; color: #031d7d; box-shadow: 0 0 0 4px rgba(3, 29, 125, 0.12); }
.ew-stepper__item.is-done .ew-stepper__dot { border-color: #031d7d; background: #031d7d; color: #fff; }

.ew-stepper__label {
  font-size: 0.74rem;
  font-weight: 500;
  color: #8a8fb3;
  text-align: center;
  max-width: 110px;
  line-height: 1.25;
}

.ew-stepper__item.is-active .ew-stepper__label { color: #212529; font-weight: 600; }
.ew-stepper__item.is-done .ew-stepper__label { color: #212529; }

.ew-summary-route {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
}

.ew-summary-route .ew-rp { flex: 1; min-width: 0; }
.ew-summary-route .ew-rp strong {
  display: block;
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ew-summary-route .ew-rp small { color: #8a8fb3; font-size: 0.72rem; }

.ew-summary-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-bottom: 12px;
}

.ew-summary-meta .ew-mc {
  text-align: center;
  padding: 6px 2px;
  border: 1px solid #eee;
  border-radius: 6px;
}
.ew-summary-meta .ew-mc strong { display: block; font-size: 0.82rem; }
.ew-summary-meta .ew-mc small { font-size: 0.62rem; color: #8a8fb3; text-transform: uppercase; }

.ew-summary-price { border-top: 1px dashed #e1e3ee; padding-top: 12px; }
.ew-summary-price .ew-pb-label { font-size: 0.7rem; color: #8a8fb3; }
.ew-summary-price .ew-pb-value { font-size: 1.1rem; font-weight: 700; }
.ew-summary-price .ew-pb-value--muted { font-size: 0.9rem; font-weight: 600; color: #8a8fb3; }
.ew-summary-price .ew-pb-sub { font-size: 0.76rem; color: #8a8fb3; margin-top: 2px; }

.ew-address-list { z-index: 1000; max-height: 250px; overflow-y: auto; }

.ew-selected-address {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  padding: 12px 14px;
}
</style>



// WEBPACK FOOTER //
// src/components/NewShipmentFlowFromDashboard.vue